import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import {
  PRICE_PERCENTAGE_DIVISOR,
  DEFAULT_PAR_VALUE_PRICING,
  SETTLEMENT_TYPES,
  SETTLEMENT_DISPLAY,
  TRANSACTION_TYPES
} from '../oms/utils/constants'
import { formatCurrency, formatDate, formatPercentage, formatPrice } from '../oms/utils/formatters'
import { PricingCalculations } from '../oms/services/pricing_calculations'
import { QuoteLineItemCalculations } from '../oms/services/quote_line_item_calculations'
import { PricingAPIService } from '../oms/services/pricing_api_service'

export default class extends Controller {
  static targets = ["lineItems", "template", "count", "sidebarCount", "summaryCount", "settlementDisplay",
                   "settlementDateField", "settlementDateInput", "settlementType", "expiryDateInput",
                   "transactionSummary", "netValueSummary", "quoteSettlement", "quoteTypeSelect", "securitySearchContainer",
                   "customNameCheckbox", "customNameField", "popoverCount", "popoverSummaryCount",
                   "popoverTransactionSummary", "popoverNetValueSummary", "popoverSettlementDisplay",
                   "pricingCalculationSelect"]

  connect() {
    console.log("OMS Quote Securities controller connected")
    this.pricingAPI = new PricingAPIService()
    this.reservedQuantities = {} // Track quantities reserved in this quote
    // Get auto-refresh threshold from data attribute (set by server) or default to 30 minutes
    this.autoRefreshThresholdMinutes = parseInt(this.element.dataset.autoRefreshThreshold) || 30
    // Get company minimum parcel size from data attribute
    this.companyMinimumParcelSize = parseInt(this.element.dataset.companyMinimumParcelSize) || 0
    // Settlement date update debouncing
    this.settlementDateTimeouts = new Map() // Track timeouts per line item
    this.initializeController()

    // Add form submission handler to convert face values to quantities
    this.setupFormSubmissionHandler()

    // Add global Enter key handler for all form inputs
    this.setupGlobalEnterKeyHandler()
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }

    // Clean up global Enter key handlers (must use same capture flag as addEventListener)
    if (this.globalEnterKeyHandler) {
      this.element.removeEventListener('keydown', this.globalEnterKeyHandler, true)
      this.element.removeEventListener('keypress', this.globalEnterKeyHandler, true)

      // Also remove from form if it exists
      if (this.formElement) {
        this.formElement.removeEventListener('keydown', this.globalEnterKeyHandler, true)
        this.formElement.removeEventListener('keypress', this.globalEnterKeyHandler, true)

        // Remove the submit preventer
        if (this.enterSubmitPreventer) {
          this.formElement.removeEventListener('submit', this.enterSubmitPreventer, true)
          this.enterSubmitPreventer = null
        }

        this.formElement = null
      }

      this.globalEnterKeyHandler = null
    }

    // Clean up form submit handler
    const form = this.element.closest('form')
    if (form && this.formSubmitHandler) {
      form.removeEventListener('submit', this.formSubmitHandler)
    }
  }

  // Helper method to get effective minimum parcel size (MAX of company and security minimum)
  getEffectiveMinimumParcelSize(securityMinParcel) {
    const securityMin = parseInt(securityMinParcel) || 0
    return Math.max(this.companyMinimumParcelSize, securityMin)
  }

  // Convert face value (dollar amount) to quantity (volume)
  convertFaceValueToQuantity(faceValue, parValue) {
    if (!faceValue || !parValue || parValue === 0) return 0
    const quantity = Math.round(faceValue / parValue)
    // Ensure minimum quantity of 1 if face value is positive
    return faceValue > 0 && quantity === 0 ? 1 : quantity
  }

  // Convert quantity (volume) to face value (dollar amount)
  convertQuantityToFaceValue(quantity, parValue) {
    if (!quantity || !parValue) return 0
    return quantity * parValue
  }

  renderSecurityDisplay(security, inventoryAvailable, greyMarketBadge = '') {
    let status, iconName, iconColor, titleText

    if (inventoryAvailable > 0) {
      if (inventoryAvailable >= 100000) {
        status = 'available'
        iconName = 'circle_check_mark'
        iconColor = 'text-green-600'
        titleText = `Inventory (${inventoryAvailable.toLocaleString()})`
      } else {
        status = 'limited'
        iconName = 'exclamation_triangle'
        iconColor = 'text-warning-600'
        titleText = `Limited (${inventoryAvailable.toLocaleString()})`
      }
    } else {
      status = 'unavailable'
      iconName = 'cross_mark'
      iconColor = 'text-red-600'
      titleText = 'No inventory'
    }

    return `
      <div class="flex items-center gap-2">
        <div class="text-sm font-medium break-words">${security.security_name || security.issuer_name || 'Unknown Security'}</div>
      </div>
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center">
          <div class="inventory-indicator"
               data-inventory-available-quantity="${inventoryAvailable}"
               title="${titleText}">
            <span class="${iconColor}">
              <svg class="w-4 h-4" fill="currentColor" stroke="white" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${this.getIconPath(iconName)}"></path>
              </svg>
            </span>
          </div>
          <div class="pl-1 text-xs text-neutral-500 break-words">ISIN: ${security.isin}</div>
        </div>
        <!-- Buy/Sell Indicator -->
        <div class="flex items-center">
          <div class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 transaction-type-indicator" title="Buy" data-transaction-type="buy">
            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
            </svg>
            Buy
          </div>
        </div>
      </div>
      ${greyMarketBadge}
    `
  }

  setupQuantityInputRestrictions() {
    // Face value inputs only allow whole numbers (no decimals)
    this.element.addEventListener('keypress', (event) => {
      if (event.target.classList.contains('face-value-input')) {
        // Allow only digits
        const char = String.fromCharCode(event.which || event.keyCode)
        if (!/[0-9]/.test(char)) {
          event.preventDefault()
        }
      }
    })

    // Add paste event handler to clean up pasted values
    this.element.addEventListener('paste', (event) => {
      if (event.target.classList.contains('face-value-input')) {
        event.preventDefault()
        const pastedText = (event.clipboardData || window.clipboardData).getData('text')
        // Remove all non-digits and convert to integer
        const cleanedValue = pastedText.replace(/[^0-9]/g, '')
        event.target.value = cleanedValue
        // Trigger validation
        this.validateFaceValue({ target: event.target })
      }
    })
  }

  setupFormSubmissionHandler() {
    // Find the form element
    const form = this.element.closest('form')
    if (!form) return

    // Store reference to handler so we can remove it when needed
    this.formSubmitHandler = async (event) => {
      console.log('Form submission - preparing form data')

      // Clear the security_ids hidden field as it's only for UI search, not for Rails
      const securityIdsField = form.querySelector('input[name*="[security_ids]"]')
      if (securityIdsField) {
        console.log(`Clearing security_ids field (was: ${securityIdsField.value})`)
        securityIdsField.value = ''
      }

      // Final cleanup of form indices
      this.updateFormIndices()

      // Check if we've already processed calculations for this submission
      if (form.dataset.calculationsProcessed === 'true') {
        console.log('Calculations already processed, allowing submission')
        // Reset the flag for next time
        delete form.dataset.calculationsProcessed
        // Allow the form to submit normally
        return
      }

      // Check if any pricing fields have pending calculations
      const pendingFields = []
      const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item')

      lineItems.forEach(lineItem => {
        // Check if spread has a value but price/yield are empty - this means calculation is needed
        const spreadInput = lineItem.querySelector('.spread-input')
        const priceInput = lineItem.querySelector('.price-input')
        const yieldInput = lineItem.querySelector('.yield-input')
        const marginInput = lineItem.querySelector('.margin-input')
        const premiumInput = lineItem.querySelector('.premium-input')

        // Check commission type
        const commissionType = this.element.dataset.commissionType || 'margin-spread'

        // If spread has been entered but price/yield are empty, we need to calculate
        // EXCEPT: In capital-price-premium mode, if premium has a value, don't calculate from spread
        // (the spread was likely calculated FROM the premium, not entered by user)
        const premiumHasValue = commissionType === 'capital-price-premium' && premiumInput && premiumInput.value && premiumInput.value.trim() !== ''

        if (spreadInput && spreadInput.value && (!priceInput?.value || !yieldInput?.value) && !premiumHasValue) {
          pendingFields.push(spreadInput)
          console.log('Found spread field with missing price/yield - needs calculation', {
            spread: spreadInput.value,
            price: priceInput?.value || 'EMPTY',
            yield: yieldInput?.value || 'EMPTY'
          })
        }
        // In capital-price-premium mode, if premium has value but yield is missing, trigger premium calculation
        else if (premiumHasValue && !yieldInput?.value) {
          pendingFields.push(premiumInput)
          console.log('Found premium field with missing yield - needs calculation', {
            premium: premiumInput.value,
            price: priceInput?.value || 'EMPTY',
            yield: yieldInput?.value || 'EMPTY'
          })
        }
        // Also check for data-cleared flag as before
        else {
          const pricingFields = lineItem.querySelectorAll('.price-input, .yield-input, .margin-input, .spread-input')
          pricingFields.forEach(field => {
            if (field.dataset.cleared === 'true') {
              pendingFields.push(field)
              console.log('Found pending calculation for field:', field.className, 'Value:', field.value)
            }
          })
        }
      })

      // If there are pending calculations, handle them
      if (pendingFields.length > 0) {
        event.preventDefault()
        console.log(`Processing ${pendingFields.length} pending field calculations before submission`)

        // Process each pending field
        for (const field of pendingFields) {
          const lineItem = field.closest('.quote-line-item')
          if (!lineItem) continue

          console.log('Triggering calculation for field:', field.className)

          // Remove the cleared flag
          field.removeAttribute('data-cleared')

          // Handle different field types
          if (field.classList.contains('spread-input')) {
            // For spread fields, run the margin calculation
            await this.calculateAndUpdateMarginFromSpread(lineItem)
          } else if (field.classList.contains('premium-input')) {
            // For premium fields, calculate capital price from premium
            await this.calculateAndUpdatePriceFromPremium(lineItem)
          } else if (field.classList.contains('margin-input')) {
            // For margin fields, calculate spread first, then pricing
            this.calculateAndUpdateSpreadFromMargin(lineItem)
            await this.calculateLineItemPricing(lineItem)
            this.updatePricingDisplay(lineItem)
            this.updateCounts()
          } else {
            // For other fields, trigger general pricing calculation
            await this.calculateLineItemPricing(lineItem)
            this.updatePricingDisplay(lineItem)
            this.updateCounts()
          }

          // IMPORTANT: After calculations, ensure form fields have the calculated values
          // The visible fields (.price-input, .yield-input, etc) should already have values
          // We need to ensure these same values are in the actual form inputs that get submitted
          const priceInput = lineItem.querySelector('.price-input')
          const yieldInput = lineItem.querySelector('.yield-input')
          const marginInput = lineItem.querySelector('.margin-input')
          const spreadInput = lineItem.querySelector('.spread-input')

          console.log('After calculation - values in visible fields:', {
            price: priceInput?.value,
            yield: yieldInput?.value,
            margin: marginInput?.value,
            spread: spreadInput?.value
          })

          // Small delay to ensure DOM is fully updated
          await new Promise(resolve => setTimeout(resolve, 50))
        }

        // Small delay to ensure DOM updates
        await new Promise(resolve => setTimeout(resolve, 100))

        console.log('Calculations complete - converting face values and resubmitting')
      }

      // Ensure all pricing fields have their values properly set
      // Sometimes the calculations update internal state but the form fields might not have the values
      console.log('Ensuring all form fields have their calculated values')
      lineItems.forEach(lineItem => {
        // Check each pricing field and log its current value
        const priceInput = lineItem.querySelector('.price-input')
        const yieldInput = lineItem.querySelector('.yield-input')
        const marginInput = lineItem.querySelector('.margin-input')
        const spreadInput = lineItem.querySelector('.spread-input')

        if (priceInput && !priceInput.value && priceInput.dataset.calculatedValue) {
          console.log('Setting price from calculated value:', priceInput.dataset.calculatedValue)
          priceInput.value = priceInput.dataset.calculatedValue
        }
        if (yieldInput && !yieldInput.value && yieldInput.dataset.calculatedValue) {
          console.log('Setting yield from calculated value:', yieldInput.dataset.calculatedValue)
          yieldInput.value = yieldInput.dataset.calculatedValue
        }

        console.log('Form field values before submission:', {
          price: priceInput?.value,
          yield: yieldInput?.value,
          margin: marginInput?.value,
          spread: spreadInput?.value
        })
      })

      // Convert face values to quantities (whether or not we had pending calculations)
      console.log('Converting face values to quantities')
      lineItems.forEach(lineItem => {
        const faceValueInput = lineItem.querySelector('.face-value-input')
        if (faceValueInput) {
          const faceValue = parseFloat(faceValueInput.value) || 0
          const parValue = parseFloat(lineItem.dataset.parValuePricing) || DEFAULT_PAR_VALUE_PRICING
          const quantity = this.convertFaceValueToQuantity(faceValue, parValue)

          console.log(`Converting face value ${faceValue} to quantity ${quantity} (par value: ${parValue})`)

          // Update the input value to be the quantity
          faceValueInput.value = quantity
        }
      })

      // If we prevented default earlier, now submit the form
      if (pendingFields.length > 0) {
        console.log('Re-submitting form after calculations')

        // Log final field values before submission
        lineItems.forEach((lineItem, idx) => {
          const priceInput = lineItem.querySelector('.price-input')
          const yieldInput = lineItem.querySelector('.yield-input')
          const marginInput = lineItem.querySelector('.margin-input')
          const spreadInput = lineItem.querySelector('.spread-input')

          console.log(`Line item ${idx} final values before re-submission:`, {
            price: priceInput?.value || 'EMPTY',
            yield: yieldInput?.value || 'EMPTY',
            margin: marginInput?.value || 'EMPTY',
            spread: spreadInput?.value || 'EMPTY',
            hasPrice: !!priceInput?.value,
            hasYield: !!yieldInput?.value
          })
        })

        // Mark that we've already processed calculations to avoid recursion
        form.dataset.calculationsProcessed = 'true'
        // Use requestSubmit to trigger proper form submission with all events
        // This will re-trigger our handler but we'll check the flag
        form.requestSubmit()
      }
    }

    // Add the submit handler
    form.addEventListener('submit', this.formSubmitHandler)
  }

  setupGlobalEnterKeyHandler() {
    // Get the form element
    const form = this.element.closest('form') || this.element.querySelector('form')

    // Create a bound handler function so we can remove it later
    this.globalEnterKeyHandler = (event) => {
      // Check if the event target is an input, select, or textarea
      const target = event.target
      const isFormField = target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA'

      if (isFormField && (event.key === 'Enter' || event.keyCode === 13)) {
        // Don't prevent Enter on textareas (they need it for line breaks)
        if (target.tagName === 'TEXTAREA') return

        // Don't prevent Enter on submit buttons
        if (target.type === 'submit') return

        // Prevent form submission
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation() // Stop all other handlers

        // Remove focus from the input
        event.target.blur()

        console.log('Prevented Enter key from submitting form on:', target.name || target.className)
        return false // Extra protection
      }
    }

    // Add handler to both the controller element and the form (if it exists)
    // Use capture phase to intercept before any other handlers
    this.element.addEventListener('keydown', this.globalEnterKeyHandler, true)
    this.element.addEventListener('keypress', this.globalEnterKeyHandler, true) // Some forms use keypress

    if (form) {
      form.addEventListener('keydown', this.globalEnterKeyHandler, true)
      form.addEventListener('keypress', this.globalEnterKeyHandler, true)

      // Also prevent form submission if triggered by Enter key
      this.enterSubmitPreventer = (event) => {
        // Check if the submission was triggered by Enter key (not by clicking submit button)
        const activeElement = document.activeElement
        if (activeElement && activeElement.tagName === 'INPUT' && activeElement.type !== 'submit') {
          console.log('Preventing form submission triggered by Enter key')
          event.preventDefault()
          event.stopPropagation()
          return false
        }
      }

      form.addEventListener('submit', this.enterSubmitPreventer, true)

      // Store reference to form for cleanup
      this.formElement = form

      console.log('Added Enter key prevention to form element')
    }
  }

  // ============== STIMULUS HELPERS ==============
  // Helper methods to replace DOM helper usage
  getLineItemField(lineItem, fieldName) {
    const fieldMap = {
      'security_id': 'input[name*="[security_id]"]',
      'position': 'input[name*="[position]"]',
      'price': '.price-input',
      'yield': '.yield-input',
      'margin': '.margin-input',
      'spread': '.spread-input',
      'quantity': '.face-value-input',
      'transaction_type': 'select[name*="[transaction_type]"]',
      'swap_rate': 'input[name*="[swap_rate]"]',
      'short_rate': 'input[name*="[short_rate]"]',
      'settlement_date': 'input[name*="[settlement_date]"]',
      'inventory_margin': 'input[name*="[inventory_margin]"]',
      'inventory_price': 'input[name*="[inventory_price]"]',
      'inventory_fetched_at': 'input[name*="[inventory_fetched_at]"]',
      'market_margin': 'input[name*="[market_margin]"]'
    }
    const selector = fieldMap[fieldName] || fieldName
    return lineItem.querySelector(selector)
  }

  getLineItemValue(lineItem, fieldName) {
    const field = this.getLineItemField(lineItem, fieldName)
    return field ? field.value : null
  }

  setLineItemValue(lineItem, fieldName, value) {
    const field = this.getLineItemField(lineItem, fieldName)
    if (field) {
      field.value = value
      return true
    }
    return false
  }

  getLineItemNumericValue(lineItem, fieldName, defaultValue = 0) {
    const value = this.getLineItemValue(lineItem, fieldName)
    const parsed = parseFloat(value)
    return isNaN(parsed) ? defaultValue : parsed
  }

  getLineItemPercentageValue(lineItem, fieldName, defaultValue = null) {
    const field = this.getLineItemField(lineItem, fieldName)
    if (!field || !field.value) return defaultValue

    const parsed = parseFloat(field.value)
    if (isNaN(parsed)) return defaultValue

    // User enters percentage, convert to decimal
    return parsed / 100
  }

  clearLineItemField(lineItem, fieldName) {
    const field = this.getLineItemField(lineItem, fieldName)
    if (field) {
      field.value = ''
      field.removeAttribute('data-cleared')
    }
  }

  // ============== NEW LINE ITEM DATA METHODS ==============
  // These methods will replace the above helpers and remove DOM dependencies

  // Extract all data from a line item using direct DOM queries
  extractLineItemData(lineItem) {
    // Get field values directly
    const priceInput = lineItem.querySelector('.price-input')
    const yieldInput = lineItem.querySelector('.yield-input')
    const marginInput = lineItem.querySelector('.margin-input')
    const spreadInput = lineItem.querySelector('.spread-input')
    const premiumInput = lineItem.querySelector('.premium-input')
    const faceValueInput = lineItem.querySelector('.face-value-input')
    const securityInput = lineItem.querySelector('input[name*="[security_id]"]')
    const transactionSelect = lineItem.querySelector('select[name*="[transaction_type]"]')
    const settlementInput = lineItem.querySelector('input[name*="[settlement_date]"]')

    // Hidden fields
    const inventoryMarginInput = lineItem.querySelector('input[name*="[inventory_margin]"]')
    const marketMarginInput = lineItem.querySelector('input[name*="[market_margin]"]')
    const swapRateInput = lineItem.querySelector('input[name*="[swap_rate]"]')
    const shortRateInput = lineItem.querySelector('input[name*="[short_rate]"]')

    const parValue = parseFloat(lineItem.dataset.parValuePricing) || DEFAULT_PAR_VALUE_PRICING
    const faceValue = faceValueInput ? parseFloat(faceValueInput.value) || 0 : 0

    // Debug spread extraction
    if (spreadInput) {
      console.log('Extracting spread:', {
        rawValue: spreadInput.value,
        isEmpty: spreadInput.value === '',
        isZero: spreadInput.value === '0' || spreadInput.value === '0.000',
        parsed: spreadInput.value ? parseFloat(spreadInput.value) : 'empty',
        asDecimal: spreadInput.value ? parseFloat(spreadInput.value) / 100 : null,
        willReturn: spreadInput.value ? parseFloat(spreadInput.value) / 100 : null
      })
    }

    // Determine which margin value to use
    let marginValue = null

    // Check if we should use precise margin
    const marginManuallyChanged = lineItem.dataset.marginManuallyChanged === 'true'
    const hasPreciseMargin = lineItem.dataset.preciseInventoryMargin !== undefined
    const displayMargin = marginInput && marginInput.value && marginInput.value.trim() !== '' ? parseFloat(marginInput.value) / 100 : null
    const preciseMargin = hasPreciseMargin ? parseFloat(lineItem.dataset.preciseInventoryMargin) : null

    // Only use precise margin if the display margin is actually present (not cleared)
    // AND the margin hasn't been manually changed
    if (!marginManuallyChanged && displayMargin !== null && preciseMargin !== null) {
      // Use precise margin if not manually changed and display has a value
      marginValue = preciseMargin
      console.log('Using precise margin for calculation:', preciseMargin, 'vs display:', displayMargin)
    } else if (displayMargin !== null) {
      // Use display margin if manually changed or no precise value
      marginValue = displayMargin
      console.log('Using display margin:', displayMargin, 'manually changed:', marginManuallyChanged)
    } else {
      // Margin field is empty/cleared - don't use any margin value
      marginValue = null
      console.log('Margin field is empty - not using any margin value')
    }

    return {
      securityId: securityInput?.value,
      transactionType: transactionSelect?.value || TRANSACTION_TYPES.BUY,
      quantity: this.convertFaceValueToQuantity(faceValue, parValue),
      faceValue: faceValue,
      price: priceInput && priceInput.value && priceInput.value.trim() !== '' ? parseFloat(priceInput.value) : null,
      // Convert percentage inputs to decimals
      yield: yieldInput && yieldInput.value ? parseFloat(yieldInput.value) / 100 : null,
      margin: marginValue,  // Use the determined margin value
      spread: spreadInput && spreadInput.value ? parseFloat(spreadInput.value) / 100 : null,
      // Premium is a dollar amount, not percentage
      premium: premiumInput && premiumInput.value ? parseFloat(premiumInput.value) : null,
      // Hidden fields are already decimals
      inventoryMargin: inventoryMarginInput && inventoryMarginInput.value ? parseFloat(inventoryMarginInput.value) : null,
      marketMargin: marketMarginInput && marketMarginInput.value ? parseFloat(marketMarginInput.value) : null,
      settlementDate: settlementInput?.value,
      parValuePricing: parseFloat(lineItem.dataset.parValuePricing || 100),
      currencyCode: lineItem.dataset.currencyCode || 'AUD',
      // Swap rates - get from input or dataset
      swapRate: this.getSwapRateValue(lineItem, swapRateInput),
      shortRate: this.getShortRateValue(lineItem, shortRateInput)
    }
  }

  // Get swap rate from input or dataset
  getSwapRateValue(lineItem, input) {
    if (input && input.value) {
      return parseFloat(input.value) // Already in percentage format
    }
    return lineItem.dataset.swapRate ? parseFloat(lineItem.dataset.swapRate) : null
  }

  getShortRateValue(lineItem, input) {
    if (input && input.value) {
      return parseFloat(input.value) // Already in percentage format
    }
    return lineItem.dataset.shortRate ? parseFloat(lineItem.dataset.shortRate) : null
  }

  // Update field with proper null handling
  updateLineItemField(lineItem, selector, value) {
    const field = lineItem.querySelector(selector)
    if (field) {
      field.value = value
      return true
    }
    return false
  }

  // Update percentage field (convert decimal to percentage for display)
  updateLineItemPercentageField(lineItem, selector, decimalValue) {
    if (decimalValue === null || decimalValue === undefined) {
      return this.updateLineItemField(lineItem, selector, '')
    }
    // Convert decimal to percentage for display
    const percentageValue = (parseFloat(decimalValue) * 100).toFixed(3)
    return this.updateLineItemField(lineItem, selector, percentageValue)
  }

  // Update text content of an element
  updateLineItemTextContent(lineItem, selector, content) {
    const element = lineItem.querySelector(selector)
    if (element) {
      element.textContent = content
    }
  }

  // Clear multiple fields at once
  clearLineItemFields(lineItem, selectors) {
    selectors.forEach(selector => {
      this.updateLineItemField(lineItem, selector, '')
    })
  }

  // Update inventory display using new pattern
  updateInventoryDisplay(lineItem) {
    const data = this.extractLineItemData(lineItem)
    const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
    const inventoryFetchedAtInput = lineItem.querySelector('input[name*="[inventory_fetched_at]"]')

    const inventoryData = QuoteLineItemCalculations.formatInventoryData(
      data.inventoryMargin,
      inventoryPriceInput ? parseFloat(inventoryPriceInput.value) : null,
      inventoryFetchedAtInput?.value
    )

    // Update inventory displays to match market row structure
    this.updateLineItemTextContent(lineItem, '.inventory-price-display', inventoryData.priceDisplay)

    // Calculate and display inventory yield if we have price and data
    if (inventoryPriceInput && inventoryPriceInput.value && data.securityId) {
      const inventoryYield = lineItem.dataset.inventoryYield

      if (inventoryYield && parseFloat(inventoryYield) > 0) {
        this.updateLineItemTextContent(lineItem, '.inventory-yield-display', `${parseFloat(inventoryYield).toFixed(3)}%`)
      } else {
        const yieldField = this.getLineItemField(lineItem, 'yield')
        const currentYield = yieldField?.value
        if (currentYield && parseFloat(currentYield) > 0) {
          this.updateLineItemTextContent(lineItem, '.inventory-yield-display', `${parseFloat(currentYield).toFixed(3)}%`)
        } else {
          this.updateLineItemTextContent(lineItem, '.inventory-yield-display', '-')
        }
      }
    } else {
      this.updateLineItemTextContent(lineItem, '.inventory-yield-display', '-')
    }

    this.updateLineItemTextContent(lineItem, '.inventory-margin-display', inventoryData.marginDisplay)

    // Display inventory source (placeholder for now)
    this.updateLineItemTextContent(lineItem, '.inventory-source-display', 'Inventory')

    // Display inventory accrued interest and principal (placeholders for now)
    this.updateLineItemTextContent(lineItem, '.inventory-accrued-display', '-')
    this.updateLineItemTextContent(lineItem, '.inventory-principal-display', '-')

    this.updateLineItemTextContent(lineItem, '.inventory-updated-display', inventoryData.fetchedAtDisplay)
  }

  // Calculate and update margin from spread
  async calculateAndUpdateMarginFromSpread(lineItem) {
    // Get the spread value that the user entered
    const spreadInput = lineItem.querySelector('.spread-input')
    if (!spreadInput) return null

    // Store the user's spread value - this should never be changed
    const userSpreadValue = spreadInput.value
    const commissionType = this.element.dataset.commissionType || 'margin-spread'

    const data = this.extractLineItemData(lineItem)

    // IMPORTANT: After adding the separate premium field, spread is ALWAYS a margin percentage
    // regardless of commission type. Premium handles the dollar adjustment in capital-price-premium mode.
    // So we always calculate margin from spread percentage:

    // For both margin-spread and capital-price-premium, calculate margin from spread percentage
    const spreadDecimal = parseFloat(userSpreadValue) / 100

    // IMPORTANT: When spread is manually changed (especially via bulk update), clear initialMargin
    // Spread should always be relative to inventory_margin (the raw inventory baseline)
    // NOT relative to initialMargin (the "precise" margin from Reset button)
    // Once user manually adjusts, they're working from inventory baseline, not reset baseline
    // This calculates: entered_margin = inventory_margin ± spread
    if (lineItem.dataset.initialMargin) {
      console.log('Clearing initialMargin - spread is now relative to inventory_margin, not precise reset value')
      delete lineItem.dataset.initialMargin
    }

    // Use initial margin (recalculated margin at inventory price) as base if available
    const initialMargin = lineItem.dataset.initialMargin ? parseFloat(lineItem.dataset.initialMargin) : null

    // IMPORTANT: inventoryMargin and marketMargin from extractLineItemData are already in decimal format
    // We need them in decimal for the calculation
    let baseMargin = initialMargin || data.inventoryMargin || data.marketMargin

    // Defensive check: if baseMargin is > 1, it's likely in percentage format instead of decimal
    // This can happen if the data was stored before our fixes
    if (baseMargin !== null && baseMargin > 1) {
      console.log('Warning: baseMargin appears to be in percentage format, converting to decimal:', baseMargin)
      baseMargin = baseMargin / 100
    }

    console.log('calculateAndUpdateMarginFromSpread:', {
      commissionType: commissionType,
      userSpreadValue: userSpreadValue,
      spreadDecimal: spreadDecimal,
      baseMargin: baseMargin,
      baseMarginPercent: baseMargin ? (baseMargin * 100).toFixed(3) + '%' : null,
      initialMargin: initialMargin,
      inventoryMargin: data.inventoryMargin,
      marketMargin: data.marketMargin
    })

    if (baseMargin !== null && !isNaN(spreadDecimal)) {
      const margin = data.transactionType === TRANSACTION_TYPES.BUY
        ? baseMargin - spreadDecimal
        : baseMargin + spreadDecimal

      // Clear premium when user changes spread (they're choosing to control via spread)
      const premiumInput = lineItem.querySelector('.premium-input')
      if (premiumInput && commissionType === 'capital-price-premium') {
        premiumInput.value = ''
        console.log('Cleared premium field since user is now controlling via spread')
      }

      // Update margin field for all modes
      // Mark as programmatic update to prevent triggering spread recalculation
      const marginInput = lineItem.querySelector('.margin-input')
      if (marginInput) {
        marginInput.dataset.programmaticUpdate = 'true'
      }
      this.updateLineItemPercentageField(lineItem, '.margin-input', margin)
      if (marginInput) {
        delete marginInput.dataset.programmaticUpdate
      }
      console.log(`Updated margin from spread: ${(margin * 100).toFixed(3)}%`, {
        spread: spreadDecimal,
        baseMargin: baseMargin,
        calculatedMargin: margin
      })

      // In all modes, clear price and yield to force backend calculation from margin
      console.log('Clearing price and yield fields to force backend calculation from adjusted margin')
      this.clearLineItemFields(lineItem, ['.price-input', '.yield-input'])

      // Mark that we need to preserve the spread during recalculation
      spreadInput.dataset.preserve = 'true'

      // Trigger pricing recalculation
      await this.calculateLineItemPricing(lineItem)
      this.updatePricingDisplay(lineItem)
      this.updateCounts()

      // Log the values after calculation
      const priceField = lineItem.querySelector('.price-input')
      const yieldField = lineItem.querySelector('.yield-input')
      const marginField = lineItem.querySelector('.margin-input')

      console.log('After pricing calculation in calculateAndUpdateMarginFromSpread:', {
        price: priceField?.value,
        yield: yieldField?.value,
        margin: marginField?.value,
        spread: spreadInput?.value
      })

      // IMPORTANT: Restore the user's spread value after all calculations
      // This ensures it's never modified by any calculation
      spreadInput.value = userSpreadValue
      spreadInput.dataset.preserve = 'false'

      return margin
    }

    return null
  }

  // Ensure zero-spread price exists for premium calculations
  async ensureZeroSpreadPriceExists(lineItem) {
    // If we already have zero-spread price, we're good
    if (lineItem.dataset.zeroSpreadPrice) {
      console.log('Zero-spread price already exists:', lineItem.dataset.zeroSpreadPrice)
      return
    }

    // If we have inventory, use inventory price as zero-spread baseline
    const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
    if (inventoryPriceInput && inventoryPriceInput.value) {
      lineItem.dataset.zeroSpreadPrice = inventoryPriceInput.value
      console.log('Using inventory price as zero-spread baseline:', inventoryPriceInput.value)
      return
    }

    // For non-inventory securities, calculate the zero-spread price
    const data = this.extractLineItemData(lineItem)

    // Get base margin (the margin at spread=0)
    // This is either market margin or initial margin
    const baseMargin = data.marketMargin !== null && data.marketMargin !== undefined
      ? data.marketMargin
      : (lineItem.dataset.initialMargin ? parseFloat(lineItem.dataset.initialMargin) : null)

    if (baseMargin === null && baseMargin !== 0) {
      console.log('No base margin available, cannot calculate zero-spread price')
      return
    }

    console.log('Calculating zero-spread price for non-inventory security, baseMargin:', baseMargin)

    const params = {
      security_id: data.securityId,
      settlement_date: data.settlementDate || this.getQuoteSettlementDate(),
      transaction_type: data.transactionType,
      margin: baseMargin,
      market_margin: data.marketMargin,
      swap_rate: data.swapRate ? data.swapRate / 100 : null,
      short_rate: data.shortRate ? data.shortRate / 100 : null
    }

    // Include quote_id for commission type determination
    const quoteId = this.element.dataset.quoteId
    if (quoteId) {
      params.quote_id = quoteId
    }

    try {
      const result = await this.pricingAPI.calculatePricing(params)
      lineItem.dataset.zeroSpreadPrice = result.price
      console.log('Calculated and stored zero-spread price:', result.price)
    } catch (error) {
      console.error('Failed to calculate zero-spread price:', error)
    }
  }

  // Calculate premium from capital price after spread-based calculation
  async calculatePremiumFromPrice(lineItem) {
    const premiumInput = lineItem.querySelector('.premium-input')
    const priceInput = lineItem.querySelector('.price-input')
    const transactionType = lineItem.querySelector('select[name*="[transaction_type]"]')?.value || 'buy'

    if (!premiumInput) return

    // Get the current capital price (which was calculated from margin with spread)
    const currentPrice = priceInput ? parseFloat(priceInput.value) : null
    if (!currentPrice || currentPrice <= 0) return

    // Ensure we have a zero-spread baseline for non-inventory securities
    await this.ensureZeroSpreadPriceExists(lineItem)

    // Get the baseline price (inventory price for inventory securities, or zero-spread price for others)
    let baselinePrice = null

    // First try inventory price
    const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
    const inventoryPrice = inventoryPriceInput ? parseFloat(inventoryPriceInput.value) : null

    if (inventoryPrice && inventoryPrice > 0) {
      baselinePrice = inventoryPrice
      console.log('Using inventory price as baseline:', baselinePrice)
    } else {
      // For non-inventory securities, use the price at zero spread as baseline
      // This would be the market price at the current settlement date with zero markup

      // First try the zero-spread price (price when spread = 0)
      if (lineItem.dataset.zeroSpreadPrice) {
        baselinePrice = parseFloat(lineItem.dataset.zeroSpreadPrice)
        console.log('Using zero-spread price as baseline:', baselinePrice)
      }
      // Then try initial capital price (stored when the line item was first loaded)
      // NOTE: This is a fallback and may not be accurate if settlement date or rates have changed
      else if (lineItem.dataset.initialCapitalPrice) {
        baselinePrice = parseFloat(lineItem.dataset.initialCapitalPrice)
        console.log('WARNING: Using initial capital price as baseline (may be inaccurate):', baselinePrice)
      }
      // Or use market price if available (T-1 price, not current)
      else if (lineItem.dataset.marketPrice) {
        baselinePrice = parseFloat(lineItem.dataset.marketPrice)
        console.log('WARNING: Using market price as baseline (T-1, may be inaccurate):', baselinePrice)
      }

      if (!baselinePrice || baselinePrice <= 0) {
        console.log('No baseline price available for premium calculation')
        // For non-inventory securities without a baseline, we can't calculate premium meaningfully
        premiumInput.value = ''
        return
      }
    }

    // Calculate premium as the difference between current price and baseline
    // For buy: premium = capital price - baseline price
    // For sell: premium = baseline price - capital price
    const premium = transactionType === 'buy'
      ? currentPrice - baselinePrice
      : baselinePrice - currentPrice

    console.log('Calculating premium from capital price:', {
      baselinePrice: baselinePrice,
      capitalPrice: currentPrice,
      transactionType: transactionType,
      calculatedPremium: premium
    })

    // Update premium field (3 decimal places to match other fields)
    premiumInput.value = premium.toFixed(3)
    // Don't set cleared flag - we want this value to be displayed
  }

  // Calculate spread from margin after premium-based calculation
  async calculateSpreadFromMargin(lineItem) {
    const spreadInput = lineItem.querySelector('.spread-input')
    const marginInput = lineItem.querySelector('.margin-input')
    const transactionType = lineItem.querySelector('select[name*="[transaction_type]"]')?.value || 'buy'

    if (!spreadInput || !marginInput) {
      console.log('calculateSpreadFromMargin: Missing spread or margin input')
      return
    }

    // Parse margin value (already in percentage format in the input)
    const marginValue = marginInput.value
    console.log('calculateSpreadFromMargin: margin input value:', marginValue)

    if (!marginValue || marginValue === '') {
      console.log('calculateSpreadFromMargin: Margin value is empty')
      return
    }

    const currentMargin = parseFloat(marginValue) / 100
    if (isNaN(currentMargin)) {
      console.log('calculateSpreadFromMargin: Invalid margin value:', marginValue)
      return
    }

    // Get the base margin (inventory or market)
    const data = this.extractLineItemData(lineItem)
    const initialMargin = lineItem.dataset.initialMargin ? parseFloat(lineItem.dataset.initialMargin) : null

    // IMPORTANT: All margins should be in decimal format for calculations
    // inventoryMargin is the true baseline for securities with inventory
    // initialMargin might be slightly different due to rounding or market conditions when calculated
    // Prioritize inventoryMargin over initialMargin for consistency with backend
    let baseMargin = data.inventoryMargin || initialMargin || data.marketMargin

    if (baseMargin === null) {
      console.log('No base margin available for spread calculation')
      return
    }

    // Defensive check: if baseMargin is > 1, it's likely in percentage format
    // This can happen with marketMargin from some backend responses
    if (baseMargin > 1) {
      console.log('Converting baseMargin from percentage to decimal:', baseMargin)
      baseMargin = baseMargin / 100
    }

    // Calculate spread as the difference between base margin and current margin
    // Both margins are in decimal format now
    // For buy: spread = base margin - current margin
    // For sell: spread = current margin - base margin
    const spread = transactionType === 'buy'
      ? baseMargin - currentMargin
      : currentMargin - baseMargin

    console.log('Calculating spread from margin:', {
      baseMargin: baseMargin,
      baseMarginPercent: (baseMargin * 100).toFixed(3) + '%',
      baseMarginSource: data.inventoryMargin ? 'inventory' : (initialMargin ? 'initial' : 'market'),
      currentMargin: currentMargin,
      currentMarginPercent: (currentMargin * 100).toFixed(3) + '%',
      transactionType: transactionType,
      calculatedSpread: spread,
      calculatedSpreadPercent: (spread * 100).toFixed(3) + '%'
    })

    // Update spread field (convert decimal to percentage for display)
    spreadInput.value = (spread * 100).toFixed(3)
    console.log('Set spread input value to:', spreadInput.value)
    // Don't set cleared flag - we want this value to be displayed
  }

  // Calculate and update price from premium (capital-price-premium mode)
  async calculateAndUpdatePriceFromPremium(lineItem) {
    const premiumInput = lineItem.querySelector('.premium-input')
    if (!premiumInput || !premiumInput.value) {
      console.log('No premium value to process')
      return
    }

    const premiumValue = parseFloat(premiumInput.value) || 0
    console.log('Premium field - updating capital price based on inventory + premium')

    // Validate premium value
    this.validatePremium(lineItem, premiumValue)

    // Mark that premium was manually changed so we know to update spread after calculation
    lineItem.dataset.premiumManuallyChanged = 'true'

    // Clear spread flag - spread and premium are mutually exclusive as user input sources
    delete lineItem.dataset.spreadManuallyChanged

    // Get the inventory/base price
    const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
    const priceInput = lineItem.querySelector('.price-input')
    const transactionType = lineItem.querySelector('select[name*="[transaction_type]"]')?.value || 'buy'

    // Try multiple ways to get inventory price
    let inventoryPrice = 0

    // First try the hidden input field
    if (inventoryPriceInput && inventoryPriceInput.value) {
      inventoryPrice = parseFloat(inventoryPriceInput.value)
      console.log(`Found inventory price from hidden input: ${inventoryPrice}`)
    }
    // Then try the data attribute we store when inventory is loaded
    else if (lineItem.dataset.inventoryPrice) {
      inventoryPrice = parseFloat(lineItem.dataset.inventoryPrice)
      console.log(`Found inventory price from inventory data attribute: ${inventoryPrice}`)
    }
    // Try any other initial price data attribute
    else if (lineItem.dataset.initialPrice) {
      inventoryPrice = parseFloat(lineItem.dataset.initialPrice)
      console.log(`Found inventory price from initial price data attribute: ${inventoryPrice}`)
    }
    // For non-inventory securities, use zero-spread price as the baseline
    else if (lineItem.dataset.zeroSpreadPrice) {
      inventoryPrice = parseFloat(lineItem.dataset.zeroSpreadPrice)
      console.log(`Using zero-spread price as base (no inventory): ${inventoryPrice}`)
    }
    // Try the initial capital price we stored on load (last resort - may include spread)
    else if (lineItem.dataset.initialCapitalPrice) {
      inventoryPrice = parseFloat(lineItem.dataset.initialCapitalPrice)
      console.log(`Using initial capital price as base (fallback - may include spread): ${inventoryPrice}`)
    }
    // If still no inventory price, try to extract it from the current state
    else if (priceInput && priceInput.value) {
      // If we don't have an inventory price stored, use the current capital price
      // For non-inventory securities, the "base price" is just the current price
      const currentPrice = parseFloat(priceInput.value)
      inventoryPrice = currentPrice
      console.log(`Using current price as base (fallback): ${inventoryPrice}`)
    }
    else {
      console.log('WARNING: Could not find any base price for premium calculation')
    }

    if (inventoryPrice > 0 && priceInput) {
      // Calculate the new capital price based on inventory + premium
      let newCapitalPrice
      if (transactionType === 'buy') {
        newCapitalPrice = inventoryPrice + premiumValue
      } else {
        newCapitalPrice = inventoryPrice - premiumValue
      }

      console.log('Setting capital price from premium:', {
        inventoryPrice,
        premium: premiumValue,
        transactionType,
        newCapitalPrice
      })

      // Update the price field with the new capital price (inventory + premium)
      priceInput.value = newCapitalPrice.toFixed(3)
      priceInput.dataset.priceSetFromPremium = 'true'
      priceInput.dataset.skipPremium = 'true'  // Don't send premium to backend to avoid double application
      console.log('Set capital price to inventory + premium:', newCapitalPrice)

      // Clear yield and margin fields since price has changed
      const yieldInput = lineItem.querySelector('.yield-input')
      const marginInput = lineItem.querySelector('.margin-input')
      if (yieldInput) {
        yieldInput.value = ''
        yieldInput.dataset.cleared = 'true'
      }
      if (marginInput) {
        marginInput.value = ''
        marginInput.dataset.cleared = 'true'
      }

      // Mark that we should skip sending premium to backend (it's already in the price)
      priceInput.dataset.skipPremium = 'true'

      console.log('Updated capital price from premium - will calculate with adjusted price')

      // Trigger pricing calculation with the adjusted price
      await this.calculateLineItemPricing(lineItem)
      this.updatePricingDisplay(lineItem)
      this.updateCounts()

      // Clean up the flag
      delete priceInput.dataset.skipPremium
    } else {
      console.log('Cannot update capital price from premium - missing inventory price')

      // Clear price and yield to force backend to recalculate from margin
      const yieldInput = lineItem.querySelector('.yield-input')
      if (priceInput) {
        priceInput.value = ''
        priceInput.dataset.cleared = 'true'
      }
      if (yieldInput) {
        yieldInput.value = ''
        yieldInput.dataset.cleared = 'true'
      }

      // Recalculate
      await this.calculateLineItemPricing(lineItem)
      this.updatePricingDisplay(lineItem)
      this.updateCounts()
    }
  }

  // Calculate and update spread from margin
  calculateAndUpdateSpreadFromMargin(lineItem, force = false) {
    // Don't recalculate if we're initializing and already have a spread value
    // UNLESS force=true (user is actively typing in margin field)
    const spreadInput = lineItem.querySelector('.spread-input')
    if (!force && spreadInput && spreadInput.value && !spreadInput.dataset.userChanged) {
      console.log('Skipping spread recalculation - existing value present:', spreadInput.value)
      return parseFloat(spreadInput.value) / 100
    }

    const data = this.extractLineItemData(lineItem)

    // Use initial margin (recalculated margin at inventory price) as base if available
    // This ensures spread = 0 when margin equals the initial quoted margin
    const initialMargin = lineItem.dataset.initialMargin ? parseFloat(lineItem.dataset.initialMargin) : null
    let baseMargin = initialMargin || data.inventoryMargin || data.marketMargin

    // Defensive check: if baseMargin is > 1, it's likely in percentage format
    // This can happen with marketMargin from some backend responses
    if (baseMargin !== null && baseMargin > 1) {
      console.log('Converting baseMargin from percentage to decimal in calculateAndUpdateSpreadFromMargin:', baseMargin)
      baseMargin = baseMargin / 100
    }

    console.log('calculateAndUpdateSpreadFromMargin:', {
      margin: data.margin,
      baseMargin: baseMargin,
      initialMargin: initialMargin,
      inventoryMargin: data.inventoryMargin,
      marketMargin: data.marketMargin,
      hasExistingSpread: spreadInput?.value
    })

    if (baseMargin !== null && data.margin !== null) {
      const spread = data.transactionType === TRANSACTION_TYPES.BUY
        ? baseMargin - data.margin
        : data.margin - baseMargin

      if (spreadInput) {
        // Convert decimal to percentage for display
        spreadInput.value = (spread * 100).toFixed(3)
        spreadInput.dataset.cleared = 'true'
      }

      console.log('Calculated spread:', {
        margin: data.margin,
        baseMargin: baseMargin,
        initialMargin: initialMargin,
        inventoryMargin: data.inventoryMargin,
        spread: spread,
        transactionType: data.transactionType
      })

      return spread
    }

    return null
  }

  // Update swap rates
  updateSwapRates(lineItem, swapRate, shortRate) {
    if (swapRate !== null && swapRate !== undefined && !isNaN(swapRate)) {
      const swapRateValue = parseFloat(swapRate)
      lineItem.dataset.swapRate = swapRateValue
      this.updateLineItemField(lineItem, 'input[name*="[swap_rate]"]', swapRateValue.toFixed(3))
    } else {
      lineItem.dataset.swapRate = ''
      this.clearLineItemField(lineItem, 'swap_rate')
    }

    if (shortRate !== null && shortRate !== undefined && !isNaN(shortRate)) {
      const shortRateValue = parseFloat(shortRate)
      lineItem.dataset.shortRate = shortRateValue
      this.updateLineItemField(lineItem, 'input[name*="[short_rate]"]', shortRateValue.toFixed(3))
    } else {
      lineItem.dataset.shortRate = ''
      this.clearLineItemField(lineItem, 'short_rate')
    }
  }

  // Clear related fields when user types
  clearRelatedFields(lineItem, fieldType) {
    // In capital-price-premium mode, price and premium are bidirectionally linked
    const commissionType = this.element.dataset.commissionType || 'margin-spread'
    const isCapitalPricePremium = commissionType === 'capital-price-premium'

    const fieldsToClear = {
      price: isCapitalPricePremium
        ? ['.yield-input', '.margin-input', '.spread-input'] // Don't clear premium when price changes (price is set FROM premium)
        : ['.yield-input', '.margin-input', '.spread-input'],
      yield: isCapitalPricePremium
        ? ['.price-input', '.margin-input', '.premium-input']  // Clear premium when yield changes (premium will be recalculated from new price)
        : ['.price-input', '.margin-input', '.spread-input'],
      margin: isCapitalPricePremium
        ? ['.price-input', '.yield-input', '.premium-input']  // Clear premium when margin changes (premium will be recalculated from new price)
        : ['.price-input', '.yield-input'],
      spread: ['.price-input', '.yield-input'],
      premium: ['.yield-input', '.margin-input']  // Clear yield/margin when premium changes (they need recalc)
    }

    const toClear = fieldsToClear[fieldType] || []
    this.clearLineItemFields(lineItem, toClear)

    // When user changes yield/margin/price, clear the premium userChanged flag
    // This allows premium to be recalculated based on the new capital price
    if (isCapitalPricePremium && (fieldType === 'yield' || fieldType === 'margin' || fieldType === 'price')) {
      const premiumInput = lineItem.querySelector('.premium-input')
      if (premiumInput && premiumInput.dataset.userChanged) {
        console.log(`Clearing premium userChanged flag because ${fieldType} was changed`)
        delete premiumInput.dataset.userChanged
      }
      // Also clear the lineItem-level flags to prevent stale calculations
      if (lineItem.dataset.premiumManuallyChanged) {
        console.log(`Clearing premiumManuallyChanged flag because ${fieldType} was changed`)
        delete lineItem.dataset.premiumManuallyChanged
      }
      if (lineItem.dataset.spreadManuallyChanged) {
        console.log(`Clearing spreadManuallyChanged flag because ${fieldType} was changed`)
        delete lineItem.dataset.spreadManuallyChanged
      }
    }
  }

  updateLineItemDisplay(lineItem, selector, content) {
    const element = lineItem.querySelector(selector)
    if (element) {
      element.textContent = content
    }
  }

  extractLineItemIndex(lineItem) {
    const anyInput = lineItem.querySelector('input[name*="[quote_line_items_attributes]"]')
    if (anyInput) {
      const match = anyInput.name.match(/\[(\d+)\]/)
      return match ? match[1] : null
    }
    return null
  }

  toggleLineItemElement(lineItem, selector, show) {
    const element = lineItem.querySelector(selector)
    if (element) {
      if (show) {
        element.classList.remove('hidden')
      } else {
        element.classList.add('hidden')
      }
    }
  }

  // ============== INITIALIZATION ==============
  initializeController() {
    this.initializeLineItemCalculations()
    this.updateCounts()
    this.updateSettlementDisplay()
    this.updateQuoteSettlementDisplays()
    this.initializeSortable()
    this.initializeSettlementOverrides()
    this.setupEventListeners()
    this.setupGlobalSpreadControls()
    this.setupQuantityInputRestrictions()
    this.checkAndAutoRefreshStaleData()
    this.autoPopulateSecurities()
    // Load inventory data for existing line items (for edit page)
    this.loadInventoryForExistingLineItems()
    // Delay validation to ensure all pricing calculations are complete
    setTimeout(() => this.validateAllSpreads(), 500)
  }

  validateAllSpreads() {
    if (!this.hasLineItemsTarget) return

    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    lineItems.forEach(lineItem => {
      const spreadInput = lineItem.querySelector('.spread-input')
      if (spreadInput && spreadInput.value) {
        const spreadValue = parseFloat(spreadInput.value) || 0
        this.validateSpread(lineItem, spreadValue)
      }
    })
  }

  setupEventListeners() {
    // Settlement type/date changes
    document.addEventListener('change', this.handleDocumentChange.bind(this))

    // Line item changes
    this.element.addEventListener('change', this.handleLineItemChange.bind(this))
    this.element.addEventListener('input', this.handleLineItemInput.bind(this))

    // Global spread event
    this.element.addEventListener('oms--global-spread:spread-applied', this.handleGlobalSpreadApplied.bind(this))
  }

  handleDocumentChange(event) {
    if (event.target.matches('select[name="oms_quote[settlement_type]"]') ||
        event.target.matches('input[name="oms_quote[default_settlement_date]"]')) {
      this.updateQuoteSettlementDisplays()

      // If settlement date OR settlement type changed, update pricing for all line items
      // (both affect settlement calculations and accrued interest)
      if (event.target.matches('input[name="oms_quote[default_settlement_date]"]') ||
          event.target.matches('select[name="oms_quote[settlement_type]"]')) {
        console.log('Settlement changed, updating pricing for all line items')
        this.updatePricingForAllLineItems()
      }
    }
  }

  async handleLineItemChange(event) {
    const lineItem = event.target.closest('.quote-line-item')
    if (!lineItem) return

    if (event.target.matches('select[name*="[transaction_type]"], input[name*="[quantity]"], input[name*="[price]"], input[name*="[spread]"], input[name*="[yield]"], input[name*="[margin]"]')) {
      console.log('Change detected on:', event.target.name, 'Value:', event.target.value)

      // If transaction type changed, we need to update inventory data
      if (event.target.matches('select[name*="[transaction_type]"]')) {
        this.handleTransactionTypeChange(lineItem)
      }

      // Handle spread field on blur/change (after user finishes editing)
      if (event.target.matches('input[name*="[spread]"]')) {
        await this.calculateAndUpdateMarginFromSpread(lineItem)
      }

      this.updateLineItemCalculations(lineItem)
      if (event.target.matches('input[name*="[quantity]"]')) {
        this.updatePricingDisplay(lineItem)
      }
      this.updateCounts()
    }

    // Handle settlement date changes with debouncing to avoid intermediate invalid dates
    if (event.target.matches('input[name*="[settlement_date]"]')) {
      console.log('Line item settlement date changed - using change event (final value)')

      // Clear zero-spread price when settlement date changes
      // It needs to be recalculated for the new settlement date
      const hasInventory = lineItem.querySelector('input[name*="[inventory_price]"]')?.value
      if (!hasInventory && lineItem.dataset.zeroSpreadPrice) {
        delete lineItem.dataset.zeroSpreadPrice
        console.log('Cleared zero-spread price due to manual settlement date change')
      }

      // For change event, update immediately since it fires when user is done editing
      this.updatePricingDisplay(lineItem)
    }
  }

  async handleLineItemInput(event) {
    const lineItem = event.target.closest('.quote-line-item')
    if (!lineItem) return

    if (event.target.matches('input[name*="[quantity]"], input[name*="[price]"], input[name*="[spread]"], input[name*="[yield]"], input[name*="[margin]"], input[name*="[settlement_date]"]')) {
      if (event.target.matches('input[name*="[spread]"]')) {
        // Don't do anything on input for spread - wait for blur
        // This prevents the field from being modified while the user is typing
        return
      } else if (event.target.matches('input[name*="[settlement_date]"]')) {
        // Use debouncing for settlement date to avoid intermediate invalid dates
        console.log('Settlement date input detected - using debounced update')
        this.debouncedSettlementDateUpdate(lineItem)
        return
      } else {
        this.updateLineItemCalculations(lineItem)
        this.updateCounts()
      }
    }
  }

  handleGlobalSpreadApplied(event) {
    console.log('Global spread applied:', event.detail)
    this.updateAllLineItemCalculations()
    // Delay updateCounts to allow async pricing calculations to complete
    setTimeout(() => {
      this.updateCounts()
    }, 100)
  }

  // Update quote dates when grey market security is added
  updateQuoteDatesForGreyMarket(security) {
    console.log('Grey market security detected, updating quote dates')
    console.log('Issue date:', security.issue_date)

    if (!security.issue_date) {
      console.warn('Grey market security missing issue_date')
      return
    }

    // Update quote type to Grey Market
    if (this.hasQuoteTypeTarget) {
      const currentType = this.quoteTypeTarget.value
      if (currentType !== 'grey_market') {
        console.log('Changing quote type to grey_market')
        this.quoteTypeTarget.value = 'grey_market'
        // Trigger change event to update any dependent UI
        this.quoteTypeTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    // Update settlement type to Negotiated
    if (this.hasSettlementTypeTarget) {
      const currentSettlementType = this.settlementTypeTarget.value
      if (currentSettlementType !== 'negotiated') {
        console.log('Changing settlement type to negotiated')
        this.settlementTypeTarget.value = 'negotiated'
        this.settlementTypeTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    // Update settlement date to issue date
    if (this.hasSettlementDateInputTarget) {
      console.log('Setting settlement date to issue date:', security.issue_date)
      this.settlementDateInputTarget.value = security.issue_date
      this.settlementDateInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      // Update the display if it exists
      const displayElement = this.element.querySelector('.settlement-date-display')
      if (displayElement) {
        const formattedDate = new Date(security.issue_date).toLocaleDateString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
        displayElement.textContent = formattedDate
      }
    }

    // Update expiry date to issue date at end of day
    if (this.hasExpiryDateTarget && this.hasExpiryTimeTarget) {
      console.log('Setting expiry to issue date end of day')
      this.expiryDateTarget.value = security.issue_date
      this.expiryTimeTarget.value = '17:00' // 5 PM
      this.expiryDateTarget.dispatchEvent(new Event('change', { bubbles: true }))
      this.expiryTimeTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Show a message to the user
    const messageContainer = document.createElement('div')
    messageContainer.className = 'bg-purple-50 border border-purple-200 text-purple-800 px-4 py-3 rounded mb-4'
    messageContainer.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-sm">Grey market security added. Settlement and expiry dates have been set to the issue date (${new Date(security.issue_date).toLocaleDateString('en-AU')}).</span>
      </div>
    `

    // Insert message at the top of the form
    const quoteDetails = this.element.querySelector('.quote-details, [data-quote-details]')
    if (quoteDetails) {
      // Remove any existing grey market messages
      const existingMessage = this.element.querySelector('.bg-purple-50')
      if (existingMessage) existingMessage.remove()

      quoteDetails.insertAdjacentElement('beforebegin', messageContainer)

      // Auto-remove message after 5 seconds
      setTimeout(() => messageContainer.remove(), 5000)
    }
  }

  // ============== SECURITY MANAGEMENT ==============
  async addSecurity(event) {
    event.preventDefault()

    const security = event.detail
    console.log("Adding security:", security)

    if (this.securityExists(security.id)) {
      console.log("Security already added")
      return
    }

    const timestamp = new Date().getTime()
    const newLineItem = this.createLineItemFromTemplate(timestamp)

    // Wait for populateLineItem to complete (including pricing fetch and updateCounts)
    await this.populateLineItem(newLineItem, security, timestamp)
    this.setLineItemPosition(newLineItem)
    // No need to call updateSecurityCountDisplay since updateCounts() was already
    // called in populateLineItem's promise chain
    this.updateFormIndices()
    this.clearSearchInput()
  }

  createLineItemFromTemplate(timestamp) {
    const template = this.templateTarget.content.cloneNode(true)
    const tempDiv = document.createElement('div')
    tempDiv.appendChild(template)
    const html = tempDiv.innerHTML.replace(/NEW_RECORD/g, timestamp)

    this.lineItemsTarget.insertAdjacentHTML('beforeend', html)
    return this.lineItemsTarget.lastElementChild
  }

  populateLineItem(lineItem, security, index) {
    console.log("Populating line item with security:", security)

    // Check if this is a grey market security and update quote dates
    if (security.is_grey_market && security.issue_date) {
      this.updateQuoteDatesForGreyMarket(security)
    }

    // Set security ID
    this.setLineItemValue(lineItem, 'security_id', security.id)

    // Store par value pricing
    lineItem.dataset.parValuePricing = security.par_value_pricing || DEFAULT_PAR_VALUE_PRICING

    // Store currency code
    lineItem.dataset.currencyCode = security.currency_code || 'AUD'

    // Store ISIN for security searches
    lineItem.dataset.securityIsin = security.isin

    // Store minimum denomination and parcel size
    if (security.min_denomination) {
      lineItem.dataset.minimumDenomination = security.min_denomination
    }
    if (security.min_parcel_size) {
      lineItem.dataset.minimumParcelSize = security.min_parcel_size
    }

    // Get line number
    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    const lineNumber = lineItems.length

    // Update security display
    const display = lineItem.querySelector('.security-display')
    if (display) {
      let greyMarketBadge = ''
      if (security.is_grey_market) {
        const label = security.grey_market_label || 'Grey Market'
        greyMarketBadge = `
          <div class="mt-1">
            <div class="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 border-purple-200 border rounded-full font-medium">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>${label}</span>
            </div>
          </div>
        `
      }
      // Determine inventory status
      const inventoryAvailable = security.inventory_available || 0

      display.innerHTML = this.renderSecurityDisplay(security, inventoryAvailable, greyMarketBadge)
    }

    // Document selection is now handled via the centralized modal
    // (removed old line-item level document selection)

    // Set default values
    // IMPORTANT: Don't set price/yield/margin here - let fetchMarketPricing set the correct values
    // based on whether inventory exists or not
    this.setLineItemValue(lineItem, 'spread', '0.000')
    this.setLineItemValue(lineItem, 'transaction_type', 'buy')

    // Set face value to effective minimum parcel size (MAX of company and security minimum)
    const effectiveMinParcel = this.getEffectiveMinimumParcelSize(security.min_parcel_size)
    const parValue = parseFloat(lineItem.dataset.parValuePricing) || DEFAULT_PAR_VALUE_PRICING
    const defaultFaceValue = effectiveMinParcel > 0 ? effectiveMinParcel : 1000 * parValue // Default was 1000 volume
    this.setLineItemValue(lineItem, 'quantity', Math.round(defaultFaceValue).toString())

    // Set S&P rating in Security Rating field (in the extra info section)
    // The Security Rating field is in the .extra-info section
    const extraInfo = lineItem.querySelector('.extra-info')
    if (extraInfo) {
      // Find the Security Rating input by looking for the label first
      const ratingLabel = Array.from(extraInfo.querySelectorAll('label')).find(label =>
        label.textContent.trim() === 'Security Rating'
      )
      if (ratingLabel) {
        const ratingInput = ratingLabel.parentElement.querySelector('input[type="text"]')
        if (ratingInput) {
          ratingInput.value = security.sp_rating || 'NR'
          console.log(`Set security rating to: ${security.sp_rating || 'NR'}`)
        }
      }
    }

    // Initialize settlement
    this.initializeLineItemSettlement(lineItem)

    // Update pricing display
    this.updatePricingDisplay(lineItem)

    // Fetch and display market pricing, then validate
    // Return the promise so the caller can wait for it if needed
    return this.fetchMarketPricing(lineItem, security.id).then(() => {
      // Return a promise that resolves after the timeout
      return new Promise(resolve => {
        setTimeout(() => {
          // Update line item calculations to ensure all values are computed
          this.updateLineItemCalculations(lineItem)
          // Update pricing display for accrued interest and totals
          this.updatePricingDisplay(lineItem)
          // Update counts after all calculations are done
          this.updateCounts()

          // Validate face value after inventory data is loaded
          const faceValueInput = lineItem.querySelector('.face-value-input')
          if (faceValueInput && faceValueInput.value) {
            this.validateFaceValue({ target: faceValueInput })
          }

          // Validate spread after all pricing data is loaded
          const spreadInput = lineItem.querySelector('.spread-input')
          if (spreadInput && spreadInput.value) {
            const spreadValue = parseFloat(spreadInput.value) || 0
            this.validateSpread(lineItem, spreadValue)
          }

          resolve()
        }, 50)
      })
    })
  }

  removeLineItem(event) {
    event.preventDefault()
    const lineItem = event.target.closest('.quote-line-item')

    // Check if this was a grey market security
    const securityDisplay = lineItem.querySelector('.security-display')
    const wasGreyMarket = securityDisplay && securityDisplay.innerHTML.includes('bg-purple-100')

    const idField = lineItem.querySelector('input[name*="[id]"]')
    const destroyField = lineItem.querySelector('input[name*="_destroy"]')

    if (idField && idField.value) {
      // This is an existing record - mark it for destruction
      console.log(`Marking line item for destruction: ID=${idField.value}`)
      if (destroyField) {
        destroyField.value = "1"
        lineItem.style.display = "none"
        // Add a data attribute to ensure we track this as deleted
        lineItem.dataset.markedForDestruction = "true"
      }
    } else {
      // This is a new record - just remove it from DOM
      console.log('Removing new line item from DOM')
      lineItem.remove()
    }

    // If we removed a grey market security, check if we need to revert the quote type
    if (wasGreyMarket) {
      this.checkAndRevertGreyMarketSettings()
    }

    this.updateCounts()
    this.updateFormIndices()

    // Update inventory availability for remaining items
    this.updateAllInventoryDisplays()
  }

  // Check if there are no more grey market securities and revert settings if needed
  checkAndRevertGreyMarketSettings() {
    const visibleLineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    let hasGreyMarketSecurities = false

    visibleLineItems.forEach(item => {
      const securityDisplay = item.querySelector('.security-display')
      if (securityDisplay && securityDisplay.innerHTML.includes('bg-purple-100')) {
        hasGreyMarketSecurities = true
      }
    })

    // If no grey market securities remain and quote type is grey_market, offer to change it
    if (!hasGreyMarketSecurities && this.hasQuoteTypeTarget && this.quoteTypeTarget.value === 'grey_market') {
      console.log('No grey market securities remaining, reverting quote type')

      // Change quote type back to standard
      this.quoteTypeTarget.value = 'standard'
      this.quoteTypeTarget.dispatchEvent(new Event('change', { bubbles: true }))

      // Also change settlement type back to T+2 if it was negotiated
      if (this.hasSettlementTypeTarget && this.settlementTypeTarget.value === 'negotiated') {
        this.settlementTypeTarget.value = 't_plus_2'
        this.settlementTypeTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }

      // Show a message to the user
      const messageContainer = document.createElement('div')
      messageContainer.className = 'bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-4'
      messageContainer.innerHTML = `
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-sm">Grey market security removed. Quote type has been changed to Standard.</span>
        </div>
      `

      // Insert message at the top of the form
      const quoteDetails = this.element.querySelector('.quote-details, [data-quote-details]')
      if (quoteDetails) {
        // Remove any existing messages
        const existingMessage = this.element.querySelector('.bg-blue-50')
        if (existingMessage) existingMessage.remove()

        quoteDetails.insertAdjacentElement('beforebegin', messageContainer)

        // Auto-remove message after 5 seconds
        setTimeout(() => messageContainer.remove(), 5000)
      }
    }
  }

  securityExists(securityId) {
    const existingInputs = this.lineItemsTarget.querySelectorAll('input[name*="[security_id]"]')
    return Array.from(existingInputs).some(input => {
      if (input.value != securityId) return false

      // Check if this line item is marked for destruction
      const lineItem = input.closest('.quote-line-item')
      if (!lineItem) return false

      // Check if line item is hidden (removed)
      if (lineItem.style.display === 'none') return false

      // Check if line item has _destroy field set to "1"
      const destroyField = lineItem.querySelector('input[name*="_destroy"]')
      if (destroyField && destroyField.value === "1") return false

      return true
    })
  }

  // Get icon path for inventory status icons
  getIconPath(iconName) {
    const iconPaths = {
      'circle_check_mark': 'M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z',
      'exclamation_triangle': 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.34 16.5c-.77.833.192 2.5 1.732 2.5z',
      'cross_mark': 'M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z'
    }
    return iconPaths[iconName] || ''
  }

  // ============== PRICING CALCULATIONS ==============
  async calculateLineItemPricing(lineItem) {
    const data = this.extractLineItemData(lineItem)

    if (!data.securityId) {
      console.log('Skipping line item - no security ID')
      return
    }

    const hasValue = data.price !== null || data.yield !== null || data.margin !== null
    if (!hasValue) {
      this.handleNoPricingData(lineItem, data)
      return
    }

    console.log('calculateLineItemPricing - data:', {
      price: data.price,
      inventoryMargin: data.inventoryMargin,
      margin: data.margin,
      spread: data.spread,
      premium: data.premium,
      hasInventoryMargin: lineItem.dataset.hasInventoryMargin
    })

    const params = this.buildPricingParams(data, lineItem)
    console.log('calculateLineItemPricing - params being sent:', params)
    console.log('calculateLineItemPricing - params keys:', Object.keys(params))
    console.log('calculateLineItemPricing - specific param values:', {
      yield: params.yield,
      margin: params.margin,
      spread: params.spread,
      inventory_margin: params.inventory_margin,
      price: params.price
    })

    try {
      const result = await this.pricingAPI.calculatePricing(params)
      console.log('calculateLineItemPricing - result:', result)
      console.log('calculateLineItemPricing - inventory margin comparison:', {
        sent: params.inventory_margin,
        received: result.margin,
        spread: result.spread
      })

      // Don't update inventory margin - keep the original value
      // This ensures we always send the original inventory margin to the backend
      // The backend will handle using it to ensure spread = 0

      await this.updatePricingFields(lineItem, result, data.inventoryMargin, data.marketMargin)
      this.updateLineItemCalculations(lineItem)
      this.updatePricingDisplay(lineItem)

      // Re-validate spread after pricing calculation is complete
      const spreadInput = lineItem.querySelector('.spread-input')
      if (spreadInput && spreadInput.value) {
        const spreadValue = parseFloat(spreadInput.value) || 0
        this.validateSpread(lineItem, spreadValue)
      }
    } catch (error) {
      console.error('Error calculating pricing:', error)
    }
  }

  buildPricingParams(data, lineItem) {
    const params = {
      security_id: data.securityId,
      settlement_date: data.settlementDate || this.getQuoteSettlementDate(),
      transaction_type: data.transactionType
    }

    // Include quote_id if available for commission control type determination
    const quoteId = this.element.dataset.quoteId
    if (quoteId) {
      params.quote_id = quoteId
    }

    // Include inventory margin when available for backend calculations
    // This is needed for correct spread calculations
    // BUT NOT when:
    // 1. Price was set from premium (we need backend to calculate new margin)
    // 2. User manually changed margin (backend will compare margin vs inventory_margin and round if close)
    const priceInput = lineItem.querySelector('.price-input')
    const shouldSkipPremium = priceInput && priceInput.dataset.skipPremium === 'true'
    const userIsChangingMargin = data.margin !== null && data.margin !== undefined
    // Check if user is changing yield (yield is set but price/margin are not)
    const userIsChangingYield = data.yield !== null && data.price === null && data.margin === null

    // Check if premium was manually changed (even if now 0)
    const premiumInput = lineItem.querySelector('.premium-input')
    const premiumWasManuallyChanged = premiumInput && premiumInput.dataset.userChanged === 'true'

    // ALWAYS include inventory_margin if it exists - backend needs it for spread calculation
    // Backend should use yield to calculate margin, and inventory_margin only for spread calculation
    // Exception: Don't send when user is changing margin (avoid backend rounding)
    if (data.inventoryMargin !== null && data.inventoryMargin !== undefined && data.inventoryMargin !== '' &&
        !userIsChangingMargin) {
      params.inventory_margin = data.inventoryMargin
      console.log('Including inventory_margin for spread calculation:', data.inventoryMargin)
      if (userIsChangingYield) {
        console.log('Note: User is changing yield - backend should calculate margin from yield, use inventory_margin only for spread')
      }
      if (premiumWasManuallyChanged) {
        console.log('Note: Premium was manually changed - backend should calculate margin from price, use inventory_margin only for spread baseline')
      }
    } else if (userIsChangingMargin) {
      console.log('NOT including inventory_margin (user is manually setting margin, avoid backend rounding)')
    }

    // Include the initial margin (recalculated margin at inventory price) if available
    // This is what the user sees as the base margin for spread calculations
    const initialMargin = lineItem.dataset.initialMargin ? parseFloat(lineItem.dataset.initialMargin) : null
    if (initialMargin !== null) {
      params.initial_margin = initialMargin
    }

    // Include market margin as fallback when no inventory margin is available
    // Market margin is already in decimal form from extractLineItemData
    if (data.marketMargin !== null && data.marketMargin !== undefined && data.marketMargin !== '') {
      params.market_margin = data.marketMargin
    }

    // Include swap rates
    // data.swapRate and data.shortRate are already in the correct format
    // They're stored as percentages in the frontend (e.g., 4.117 for 4.117%)
    // But the backend expects decimals (e.g., 0.04117)
    const swapRate = data.swapRate
    const shortRate = data.shortRate

    if (swapRate !== null) params.swap_rate = swapRate / 100  // Convert percentage to decimal
    if (shortRate !== null) params.short_rate = shortRate / 100  // Convert percentage to decimal

    // Include spread and/or premium based on commission type
    const commissionType = this.element.dataset.commissionType || 'margin-spread'

    // Check if we should skip sending premium (already defined above)
    const priceWasManuallyChanged = priceInput && priceInput.dataset.userChanged === 'true'

    // premiumWasManuallyChanged is already defined above for inventory_margin check

    // Include premium in capital-price-premium mode
    // ALWAYS send premium when it exists so backend can extract base price correctly
    // Exception: Don't send if user is changing yield (conflicting constraints)
    if (commissionType === 'capital-price-premium' &&
        data.premium !== null && data.premium !== undefined && data.premium !== 0 &&
        !userIsChangingYield) {
      params.premium = data.premium
      // When premium is provided and non-zero, DON'T send spread - let backend calculate it from premium
      console.log('Premium provided, not sending spread to allow backend calculation')

      // DON'T send spread when price was set from premium
      // Backend needs to calculate new margin/spread from the adjusted price
      if (shouldSkipPremium) {
        console.log('Price was set from premium - backend will extract base price for storage')
      }
    } else if (userIsChangingYield && commissionType === 'capital-price-premium') {
      console.log('User is changing yield - not sending premium to avoid conflicting constraints')
    } else if (priceWasManuallyChanged && commissionType === 'capital-price-premium') {
      // Price was manually changed - check if we should include spread
      console.log('Price was manually changed - checking spread availability')
      if (data.spread !== null && data.spread !== undefined) {
        params.spread = data.spread
        console.log('Including spread in params:', data.spread)
      } else {
        console.log('No spread to include')
      }
    } else {
      // Only include spread when premium is not the driver (or in margin-spread mode)
      // BUT DON'T send spread if premium was manually changed (even if now 0) - it's stale
      if (premiumWasManuallyChanged && commissionType === 'capital-price-premium') {
        console.log('Premium was manually changed - NOT sending stale spread, let backend calculate fresh values')
      } else if (data.spread !== null && data.spread !== undefined) {
        params.spread = data.spread
      }
    }

    // Determine which values to send
    // Special case: if price was set from premium (skipPremium flag), send just price
    if (shouldSkipPremium && data.price && data.yield === null && data.margin === null) {
      params.price = data.price
      // DON'T send inventory_margin when we want backend to calculate new margin from price
      // Otherwise backend might just return inventory_margin when spread is 0
      // Also remove spread if it was added - it's stale when premium changes
      if (params.spread !== undefined) {
        delete params.spread
        console.log('Removed stale spread from params since price was set from premium')
      }
      console.log('Sending price that was set from premium for calculation (no inventory_margin or spread to force fresh calculation):', params)
    }
    // Special case: if we have price and spread/premium (from refresh), send those
    else if (data.price && ((commissionType === 'capital-price-premium' && data.premium !== null && data.premium !== undefined) ||
                            (commissionType !== 'capital-price-premium' && data.spread !== null && data.spread !== undefined)) &&
             data.yield === null && data.margin === null) {
      params.price = data.price
      // spread or premium is already included above
      console.log(`Sending price and ${commissionType === 'capital-price-premium' ? 'premium' : 'spread'} for calculation`)
    } else {
      // When all three values are present (initial load or recalculate without changes),
      // we need to pick one to avoid backend confusion
      const hasAllValues = (data.price !== null && data.price !== undefined) &&
                          (data.yield !== null && data.yield !== undefined) &&
                          (data.margin !== null && data.margin !== undefined)

      if (hasAllValues) {
        // When all values are present, we need to decide what to send
        // Generally we send margin to avoid confusion
        if (data.margin !== null && data.margin !== undefined) {
          params.margin = data.margin  // Already decimal from getPercentageValue
        }

        // Include inventory margin if available
        if (data.inventoryMargin !== null && data.inventoryMargin !== undefined) {
          params.inventory_margin = data.inventoryMargin
        }

        // Include spread if available
        if (data.spread !== null && data.spread !== undefined) {
          params.spread = data.spread
        }

        console.log('All values present - sending margin for calculation:', {
          margin: params.margin,
          inventory_margin: params.inventory_margin,
          spread: params.spread
        })
      } else {
        // Special case: When spread is EXACTLY 0 and we have inventory, send the inventory price
        // This ensures we get back the exact inventory price, not a calculated one
        // BUT: Only if user hasn't manually changed price or yield
        // IMPORTANT: Use exact equality check, not a threshold - small non-zero spreads like -0.008% are valid
        const spreadIsExactlyZero = data.spread === 0
        const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
        const inventoryPrice = inventoryPriceInput ? parseFloat(inventoryPriceInput.value) : null

        // Check if user manually changed price or yield
        const priceInputField = lineItem.querySelector('.price-input')
        const yieldInputField = lineItem.querySelector('.yield-input')
        const priceUserChanged = priceInputField && priceInputField.dataset.userChanged === 'true'
        const yieldUserChanged = yieldInputField && yieldInputField.dataset.userChanged === 'true'

        if (spreadIsExactlyZero && inventoryPrice && data.inventoryMargin && !priceUserChanged && !yieldUserChanged) {
          console.log('Spread is exactly 0 with inventory - sending inventory price directly:', inventoryPrice)
          params.price = inventoryPrice
          params.inventory_margin = data.inventoryMargin
          params.spread = 0
        } else {
          // Otherwise, send whichever values are available
          // (Manual changes clear other fields, so only one will be sent)
          if (data.price !== null && data.price !== undefined) params.price = data.price
          if (data.yield !== null && data.yield !== undefined) params.yield = data.yield  // Already decimal from getPercentageValue
          if (data.margin !== null && data.margin !== undefined) params.margin = data.margin  // Already decimal from getPercentageValue

          // NOTE: inventory_margin is already handled above (lines 1489-1496)
          // It's only included when NOT sending margin manually

          // Only include spread if margin is NOT being sent AND user is not changing yield
          // When margin is sent OR yield is being changed, let the backend calculate the spread
          const shouldExcludeSpread = userIsChangingYield || (data.margin !== null && data.margin !== undefined)

          if (data.spread !== null && data.spread !== undefined && !params.spread && !shouldExcludeSpread) {
            params.spread = data.spread
            console.log('Including spread (margin and yield not being changed):', data.spread)
          } else if (data.margin !== null && data.margin !== undefined) {
            console.log('Margin is being sent - NOT sending spread, let backend calculate it')
          } else if (userIsChangingYield) {
            console.log('Yield is being changed - NOT sending spread, let backend calculate margin and spread from yield')
            // Explicitly ensure spread is not in params even if it was extracted as 0
            if (params.spread !== undefined) {
              delete params.spread
              console.log('Explicitly removed spread from params for yield change')
            }
          }
        }
      }
    }

    return params
  }

  async updatePricingFields(lineItem, data, inventoryMargin, marketMargin) {
    console.log('updatePricingFields called with:', {
      price: data.price,
      yield: data.yield,
      margin: data.margin,
      spread: data.spread,
      inventoryMargin: inventoryMargin,
      marketMargin: marketMargin
    })

    // IMPORTANT: Ensure zero-spread price exists for non-inventory securities
    // This ensures the inline premium calculation below has a proper baseline
    // regardless of which field the user changed (margin, yield, spread, or premium)
    const commissionType = this.element.dataset.commissionType || 'margin-spread'
    console.log('Commission type check:', { commissionType, element: this.element })
    if (commissionType === 'capital-price-premium') {
      console.log('BEFORE calling ensureZeroSpreadPriceExists')
      await this.ensureZeroSpreadPriceExists(lineItem)
      console.log('AFTER calling ensureZeroSpreadPriceExists, zero-spread price:', lineItem.dataset.zeroSpreadPrice)
    }

    // Store the BASE price from backend, not the premium-adjusted price
    if (data.price !== null && !isNaN(data.price)) {
      const basePrice = parseFloat(data.price)
      const commissionType = this.element.dataset.commissionType || 'margin-spread'
      const priceInput = lineItem.querySelector('input[name*="[price]"]')

      // In capital-price-premium mode: display capital_price, store base_price separately
      if (commissionType === 'capital-price-premium' && data.capital_price !== null && !isNaN(data.capital_price)) {
        const capitalPrice = parseFloat(data.capital_price)

        // Display capital price in the visible field (what user sees)
        this.setLineItemValue(lineItem, 'price', capitalPrice.toFixed(3))

        // Store base price in data attribute (used for calculations)
        if (priceInput) {
          priceInput.dataset.basePrice = basePrice.toFixed(3)
        }

        console.log('Set price field to capital price:', capitalPrice.toFixed(3),
                    `(base stored in data-base-price: ${basePrice.toFixed(3)})`)
      } else {
        // In margin-spread mode: just store and display the price as-is
        this.setLineItemValue(lineItem, 'price', basePrice.toFixed(3))
        console.log('Set price field to:', basePrice.toFixed(3))
      }

      // IMPORTANT: If spread is exactly zero, this price IS the zero-spread baseline
      // Store it for premium calculations
      // CRITICAL: Check the spread from the BACKEND RESPONSE (data.spread), not the frontend input field
      // The input field might show a preserved value that doesn't match the actual spread for this price
      const actualSpread = data.spread !== null && data.spread !== undefined ? parseFloat(data.spread) : null

      if (actualSpread !== null && Math.abs(actualSpread) < 0.0001) {
        // Don't overwrite zero-spread price if it's from inventory
        const hasInventory = lineItem.querySelector('input[name*="[inventory_price]"]')?.value
        if (!hasInventory) {
          lineItem.dataset.zeroSpreadPrice = basePrice.toString()
          console.log('Stored zero-spread price from backend spread=0 calculation:', basePrice, 'backend spread:', actualSpread)
        }
      }

      // IMPORTANT: When price is updated programmatically (from yield/margin change),
      // we need to recalculate premium to keep it in sync
      const premiumInput = lineItem.querySelector('.premium-input')
      const transactionType = lineItem.querySelector('select[name*="[transaction_type]"]')?.value || 'buy'

      if (commissionType === 'capital-price-premium' && premiumInput) {
        // Get baseline price for premium calculation
        // Priority: inventory price > zero-spread price > initial capital price (fallback)
        const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
        let baselinePrice = 0
        let baselineSource = 'unknown'

        // Priority 1: Check for actual inventory price
        if (inventoryPriceInput && inventoryPriceInput.value) {
          baselinePrice = parseFloat(inventoryPriceInput.value)
          baselineSource = 'inventory_input'
        } else if (lineItem.dataset.inventoryPrice) {
          baselinePrice = parseFloat(lineItem.dataset.inventoryPrice)
          baselineSource = 'inventory_dataset'
        }
        // Priority 2: For non-inventory securities, use zero-spread price
        else if (lineItem.dataset.zeroSpreadPrice) {
          baselinePrice = parseFloat(lineItem.dataset.zeroSpreadPrice)
          baselineSource = 'zero_spread_price'
        }
        // Priority 3: Fallback to initial price/capital price
        else if (lineItem.dataset.initialPrice) {
          baselinePrice = parseFloat(lineItem.dataset.initialPrice)
          baselineSource = 'initial_price'
        } else if (lineItem.dataset.initialCapitalPrice) {
          baselinePrice = parseFloat(lineItem.dataset.initialCapitalPrice)
          baselineSource = 'initial_capital_price'
        }

        if (baselinePrice > 0) {
          // IMPORTANT: Only recalculate premium if it wasn't manually changed by the user
          // When user changes yield/margin/price, the userChanged flag is cleared, allowing recalculation
          const premiumManuallyChanged = premiumInput.dataset.userChanged === 'true'

          if (!premiumManuallyChanged) {
            // Recalculate premium based on new price
            // IMPORTANT: Use capitalPrice (displayed price) not basePrice for premium calculation
            const displayedPrice = data.capital_price !== null && !isNaN(data.capital_price)
              ? parseFloat(data.capital_price)
              : basePrice

            let calculatedPremium
            if (transactionType === 'buy') {
              calculatedPremium = displayedPrice - baselinePrice
            } else {
              calculatedPremium = baselinePrice - displayedPrice
            }

            console.log('Recalculating premium after programmatic price update:', {
              newPrice: displayedPrice,
              baselinePrice,
              baselineSource,
              transactionType,
              calculatedPremium
            })

            // Update premium field
            premiumInput.dataset.programmaticUpdate = 'true'
            premiumInput.value = calculatedPremium.toFixed(3)
            delete premiumInput.dataset.programmaticUpdate

            // Validate the premium
            this.validatePremium(lineItem, calculatedPremium)
          } else {
            console.log('Premium was manually changed by user - keeping original value:', premiumInput.value)
          }
        }
      }
    } else {
      console.log('Skipped price update - value was null or NaN')
    }

    // Backend returns decimals, convert to percentage for display
    if (data.yield !== null && !isNaN(data.yield)) {
      this.updateLineItemPercentageField(lineItem, '.yield-input', data.yield)
      console.log('Set yield field to:', (data.yield * 100).toFixed(3))
    } else {
      console.log('Skipped yield update - value was null or NaN')
    }

    if (data.margin !== null && !isNaN(data.margin)) {
      this.updateLineItemPercentageField(lineItem, '.margin-input', data.margin)
      console.log('Set margin field to:', (data.margin * 100).toFixed(3))
    } else {
      console.log('Skipped margin update - value was null or NaN')
    }

    // Handle spread update - but preserve user's value if marked
    const spreadInput = lineItem.querySelector('.spread-input')
    const shouldPreserveSpread = spreadInput && spreadInput.dataset.preserve === 'true'

    // Check if we're in premium mode and premium was sent
    const premiumInput = lineItem.querySelector('.premium-input')
    const hasPremium = premiumInput && premiumInput.value && parseFloat(premiumInput.value) !== 0

    // Check if price was set from premium (we calculated spread from premium)
    const priceInput = lineItem.querySelector('.price-input')
    const priceSetFromPremium = priceInput && priceInput.dataset.skipPremium === 'true'

    if (shouldPreserveSpread) {
      // Don't update spread - user has manually entered a value
      console.log('Preserving user spread value:', spreadInput.value)
    } else if (priceSetFromPremium && spreadInput && spreadInput.value && hasPremium) {
      // Preserve the spread we calculated from premium ONLY if premium is non-zero
      // If premium is 0, we need fresh spread calculation
      console.log('Preserving frontend-calculated spread from premium (premium is non-zero):', spreadInput.value + '%')
    } else if (priceSetFromPremium && !hasPremium && data.spread !== null && !isNaN(data.spread)) {
      // Premium was set to 0 - use backend's calculated spread
      // This may be non-zero due to market drift since inventory was set
      this.updateLineItemPercentageField(lineItem, '.spread-input', data.spread)
      console.log('Premium is 0 - using backend spread:', (data.spread * 100).toFixed(3) + '%')
    } else if (lineItem.dataset.marginManuallyChanged === 'true' && data.margin !== null && !isNaN(data.margin) && (inventoryMargin !== null || marketMargin !== null)) {
      // User manually changed margin - recalculate spread from returned margin and base margin
      // Use inventory margin for inventory securities, market margin for non-inventory securities
      // Don't use backend's spread as it may be calculated with slightly different precision
      const baseMargin = inventoryMargin !== null ? inventoryMargin : marketMargin
      const baseMarginType = inventoryMargin !== null ? 'inventory' : 'market'
      const transactionType = this.getLineItemValue(lineItem, 'transaction_type') || TRANSACTION_TYPES.BUY
      const spreadDecimal = transactionType === TRANSACTION_TYPES.BUY
        ? baseMargin - data.margin
        : data.margin - baseMargin
      this.updateLineItemPercentageField(lineItem, '.spread-input', spreadDecimal)
      console.log('Recalculated spread from margin change:', {
        returnedMargin: (data.margin * 100).toFixed(3) + '%',
        baseMarginType: baseMarginType,
        baseMargin: (baseMargin * 100).toFixed(3) + '%',
        calculatedSpread: (spreadDecimal * 100).toFixed(3) + '%',
        backendSpread: data.spread ? (data.spread * 100).toFixed(3) + '% (ignored)' : 'N/A'
      })
      // Clear the flag now that we've recalculated - future automatic updates should use backend spread
      lineItem.dataset.marginManuallyChanged = 'false'
      console.log('Cleared marginManuallyChanged flag after recalculating spread')
    } else if (data.spread !== null && !isNaN(data.spread)) {
      // Backend returned a spread - use it
      this.updateLineItemPercentageField(lineItem, '.spread-input', data.spread)
      console.log('Updated spread from backend:', (data.spread * 100).toFixed(3) + '%')
    } else if (!inventoryMargin && !marketMargin) {
      // No base margin available - can't calculate spread
      this.updateLineItemField(lineItem, '.spread-input', '')
      console.log('Cleared spread because no inventory or market margin available')
    } else if (data.margin !== null && !isNaN(data.margin) && !hasPremium && !lineItem.dataset.premiumManuallyChanged) {
      // Calculate spread if we have margin but no spread returned and no premium
      // Don't do this if premium was manually changed (we'll calculate spread later in that case)
      // Use initial margin (recalculated margin at inventory price) as base if available
      const initialMargin = lineItem.dataset.initialMargin ? parseFloat(lineItem.dataset.initialMargin) : null
      const baseMargin = initialMargin || inventoryMargin || marketMargin
      const transactionType = this.getLineItemValue(lineItem, 'transaction_type') || TRANSACTION_TYPES.BUY
      const spreadDecimal = transactionType === TRANSACTION_TYPES.BUY
        ? baseMargin - data.margin
        : data.margin - baseMargin
      this.updateLineItemPercentageField(lineItem, '.spread-input', spreadDecimal)
      console.log(`Calculated spread during recalc: ${(spreadDecimal * 100).toFixed(3)}% (baseMargin=${baseMargin}, initialMargin=${initialMargin}, inventoryMargin=${inventoryMargin})`)
    } else if (hasPremium || lineItem.dataset.premiumManuallyChanged) {
      // Premium was sent or manually changed - spread will be calculated separately
      console.log('Premium present or manually changed - spread will be calculated from margin difference')
    }

    // Update swap rates
    // The pricing API returns swap rates in the same format we sent them (as percentages stored as decimals)
    // We send swapRate/100, backend returns the same decimal, so we need to multiply by 100 to get back to percentage
    const swapRateValue = data.swap_rate !== null && data.swap_rate !== undefined
      ? data.swap_rate * 100
      : null
    const shortRateValue = data.short_rate !== null && data.short_rate !== undefined
      ? data.short_rate * 100
      : null

    this.updateSwapRates(lineItem, swapRateValue, shortRateValue)

    // Clear data-cleared flags
    this.clearFieldFlags(lineItem)

    // In capital-price-premium mode, update the complementary field based on what was changed
    // commissionType already declared at the beginning of this function
    if (commissionType === 'capital-price-premium') {
      // If spread was manually changed, calculate premium from the returned capital price
      if (lineItem.dataset.spreadManuallyChanged === 'true') {
        console.log('Spread was manually changed - calculating premium from returned capital price')
        this.calculatePremiumFromPrice(lineItem)
        delete lineItem.dataset.spreadManuallyChanged
      }
      // If premium was manually changed, calculate spread from the returned margin
      else if (lineItem.dataset.premiumManuallyChanged === 'true') {
        console.log('Premium was manually changed - calculating spread from returned margin')
        this.calculateSpreadFromMargin(lineItem)
        delete lineItem.dataset.premiumManuallyChanged
      }
    }
  }

  handleNoPricingData(lineItem, data) {
    console.log(`Skipping security ${data.securityId} - no price, yield, or margin entered`)

    // If only spread is entered without inventory margin, clear calculated fields
    if (data.spread !== null && data.spread !== 0 && !data.inventoryMargin) {
      console.log('Spread entered but no inventory margin available for calculation')
      if (!data.price) this.clearLineItemField(lineItem, 'price')
      if (!data.yield) this.clearLineItemField(lineItem, 'yield')
      if (!data.margin) this.clearLineItemField(lineItem, 'margin')
    }
  }

  // ============== MARKET PRICING ==============
  async fetchMarketPricing(lineItem, securityId, isRecalculation = false) {
    try {
      const data = this.extractLineItemData(lineItem)

      const settlementDate = data.settlementDate || this.getQuoteSettlementDate()
      // PLAT-970: Get selected pricing calculation for margin type selection
      const pricingCalculation = this.getSelectedPricingCalculation()
      console.log(`Fetching market pricing for security ${securityId}, transaction type: ${data.transactionType}, pricing calculation: ${pricingCalculation}`)

      const pricingData = await this.pricingAPI.fetchMarketPricing(
        securityId,
        data.transactionType,
        settlementDate,
        pricingCalculation // PLAT-970: Pass selected pricing calculation
      )

      console.log('Pricing data received:', pricingData)

      if (pricingData.initial_yield !== undefined && pricingData.inventory?.has_inventory) {
        lineItem.dataset.inventoryYield = pricingData.initial_yield.toString()
      }

      this.displayMarketPricing(lineItem, pricingData)

      // Set initial values only for new line items without existing values
      const priceField = this.getLineItemField(lineItem, 'price')
      const yieldField = this.getLineItemField(lineItem, 'yield')
      const marginField = this.getLineItemField(lineItem, 'margin')

      const currentPrice = priceField?.value
      const currentYield = yieldField?.value
      const currentMargin = marginField?.value

      console.log('Current field values:', {
        price: currentPrice,
        yield: currentYield,
        margin: currentMargin,
        priceField: priceField,
        hasInventory: pricingData.inventory?.has_inventory,
        auto_recalculate: pricingData.auto_recalculate,
        initial_price: pricingData.initial_price,
        initial_yield: pricingData.initial_yield,
        initial_margin: pricingData.initial_margin
      })

      const needsInitialValues = !currentPrice && !currentYield && !currentMargin

      if (needsInitialValues && !isRecalculation) {
        console.log('Setting initial values from pricing data')
        this.setInitialPricingValues(lineItem, pricingData)

        // IMPORTANT: If we have inventory and auto_recalculate is true, we've already set the correct
        // inventory-based initial values. We should NOT auto-recalculate as that would overwrite
        // the inventory values with market-based calculations.
        if (pricingData.auto_recalculate && pricingData.inventory?.has_inventory) {
          console.log('Skipping auto-recalculation - already set inventory-based initial values')
          // Don't return early - we need to complete the method so the promise resolves
        } else {
          // Check if auto-recalculation is needed (matches old system behavior)
          // Only auto-recalculate for NEW line items without existing price/yield/margin values
          const hasExistingValues = this.getLineItemValue(lineItem, 'price') ||
                                   this.getLineItemValue(lineItem, 'yield') ||
                                   this.getLineItemValue(lineItem, 'margin')

          if (pricingData.auto_recalculate && !isRecalculation && !hasExistingValues) {
            console.log('Auto-recalculating for new line item based on inventory price difference')
            console.log('Pricing data before auto-recalc:', pricingData)
            // Trigger calculation to match old system behavior
            // This will recalculate based on the initial inventory values that were just set
            await this.calculateLineItemPricing(lineItem)
          } else if (pricingData.auto_recalculate && hasExistingValues) {
            console.log('Skipping auto-recalculation for existing line item with saved values')
          }
        }
      } else if (lineItem.dataset.updatingTransactionType === 'true') {
        if (pricingData.initial_price !== undefined) {
          const priceValue = parseFloat(pricingData.initial_price).toFixed(3)
          this.setLineItemValue(lineItem, 'price', priceValue)
        }
      } else {
        console.log('Skipping initial values - already has values or is recalculation')
      }

    } catch (error) {
      console.error('Error fetching market pricing:', error)
    }
  }

  displayMarketPricing(lineItem, pricingData) {
    if (!pricingData || !pricingData.market_price) return

    const marketPrice = pricingData.market_price

    // Store par value pricing
    if (pricingData.par_value_pricing) {
      lineItem.dataset.parValuePricing = pricingData.par_value_pricing
    }

    // Store market margin ONLY when there's no inventory (for fallback spread calculations)
    // Market margin should be stored as decimal (like inventory_margin) for consistency
    if (pricingData.market_margin !== undefined && !pricingData.inventory?.has_inventory) {
      // market_margin from the API should already be in decimal format
      const marketMarginDecimal = parseFloat(pricingData.market_margin)
      this.setLineItemValue(lineItem, 'market_margin', marketMarginDecimal)
      console.log('Storing market_margin (no inventory):', {
        apiValue: pricingData.market_margin,
        storedAsDecimal: marketMarginDecimal,
        has_inventory: false
      })
    } else if (pricingData.inventory?.has_inventory) {
      // Clear any existing market margin when we have inventory
      this.setLineItemValue(lineItem, 'market_margin', '')
      console.log('Clearing market_margin (has inventory)')
    }

    // Update displays
    this.updateLineItemDisplay(lineItem, '.market-price-display',
      marketPrice.capital_price ? `$${parseFloat(marketPrice.capital_price).toFixed(3)}` : '-')

    this.updateLineItemDisplay(lineItem, '.market-yield-display',
      marketPrice.yield_to_maturity ? `${(parseFloat(marketPrice.yield_to_maturity) * 100).toFixed(3)}%` : '-')

    // PLAT-970: Display market margin with metadata (label, badge, note)
    this.displayMarginWithMetadata(lineItem, pricingData)

    // Display pricing source
    this.updateLineItemDisplay(lineItem, '.pricing-source-display',
      marketPrice.source || '-')

    // Calculate and display market-based accrued interest and principal
    this.calculateMarketBasedPricing(lineItem, marketPrice, pricingData)

    // Display last updated timestamp for market data
    this.updateLineItemDisplay(lineItem, '.market-updated-display',
      marketPrice.date_as_at ? new Date(marketPrice.date_as_at).toLocaleString() : '-')

    this.updateLineItemDisplay(lineItem, '.price-source-display',
      marketPrice.source ? `${marketPrice.source} (${formatDate(marketPrice.date_as_at)})` : '-')

    // Always store swap rates from market data
    // Market price swap rates come as decimals from backend, convert to percentage for storage
    const swapRatePercent = marketPrice.swap_rate !== null && marketPrice.swap_rate !== undefined
      ? marketPrice.swap_rate * 100
      : null
    const shortRatePercent = marketPrice.short_rate !== null && marketPrice.short_rate !== undefined
      ? marketPrice.short_rate * 100
      : null
    this.updateSwapRates(lineItem, swapRatePercent, shortRatePercent)

    // Store inventory data
    this.storeInventoryData(lineItem, pricingData.inventory)
  }

  // Calculate market-based pricing (separate from user input pricing)
  async calculateMarketBasedPricing(lineItem, marketPrice, pricingData) {
    if (!marketPrice.capital_price) {
      console.log('No market price available, skipping market pricing calculations')
      return
    }

    const data = this.extractLineItemData(lineItem)
    const marketCapitalPrice = parseFloat(marketPrice.capital_price)
    const parValuePricing = parseFloat(pricingData.par_value_pricing) || 100

    // Market pricing section elements
    const marketAccruedSelector = '.market-pricing-section .accrued-interest-display'
    const marketPrincipalSelector = '.market-pricing-section .principal-display'
    const marketAccruedElement = lineItem.querySelector(marketAccruedSelector)
    const marketPrincipalElement = lineItem.querySelector(marketPrincipalSelector)

    console.log('Calculating market-based pricing:', {
      hasQuantity: data.quantity > 0,
      quantity: data.quantity,
      marketCapitalPrice: marketCapitalPrice,
      parValuePricing: parValuePricing
    })

    // Only calculate and display values if user has entered a quantity
    if (data.quantity > 0) {
      // Calculate market-based principal using market capital price and user's quantity
      const marketPrincipal = PricingCalculations.calculatePrincipal(
        data.quantity,
        marketCapitalPrice,
        parValuePricing
      )

      console.log('Market principal calculated:', {
        quantity: data.quantity,
        marketCapitalPrice: marketCapitalPrice,
        result: marketPrincipal
      })

      // Update market-based principal display
      if (marketPrincipalElement) {
        marketPrincipalElement.textContent = formatCurrency(marketPrincipal)
      } else {
        // Fallback to general selector if market-specific element not found
        this.updateLineItemDisplay(lineItem, '.principal-display', formatCurrency(marketPrincipal))
      }

      // Use accrued interest from market pricing data
      // For grey market securities, accrued interest should already be 0 from backend
      const accruedRate = parseFloat(marketPrice.accrued_interest) || 0
      const marketAccruedAmount = (accruedRate * data.quantity * parValuePricing) / 100

      console.log('Market accrued interest calculated:', {
        accruedRate: accruedRate,
        quantity: data.quantity,
        parValuePricing: parValuePricing,
        result: marketAccruedAmount
      })

      if (marketAccruedElement) {
        marketAccruedElement.textContent = formatCurrency(marketAccruedAmount)
      } else {
        // Fallback to general selector if market-specific element not found
        this.updateLineItemDisplay(lineItem, '.accrued-interest-display', formatCurrency(marketAccruedAmount))
      }
    } else {
      // No quantity entered yet - show dashes for market pricing
      console.log('No quantity entered, showing dashes for market pricing')

      if (marketPrincipalElement) {
        marketPrincipalElement.textContent = '-'
      } else {
        this.updateLineItemDisplay(lineItem, '.principal-display', '-')
      }

      if (marketAccruedElement) {
        marketAccruedElement.textContent = '-'
      } else {
        this.updateLineItemDisplay(lineItem, '.accrued-interest-display', '-')
      }
    }
  }

  setInitialPricingValues(lineItem, data) {
    console.log('setInitialPricingValues called with:', {
      initial_price: data.initial_price,
      initial_yield: data.initial_yield,
      initial_margin: data.initial_margin,
      has_inventory: data.inventory?.has_inventory,
      inventory_price: data.inventory?.inventory_price,
      inventory_margin: data.inventory?.inventory_margin
    })

    if (data.initial_price !== undefined) {
      const priceValue = parseFloat(data.initial_price).toFixed(3)
      this.setLineItemValue(lineItem, 'price', priceValue)
      console.log('Set price to:', priceValue)

      // Store the initial price as the base for premium calculations
      // For securities without inventory, this is the market price
      // For securities with inventory, this will be overwritten by storeInventoryData
      lineItem.dataset.initialCapitalPrice = priceValue
      console.log('Stored initial capital price for premium calculations:', priceValue)
    }

    // Backend sends yield and margin as percentages, display them as is
    if (data.initial_yield !== undefined) {
      this.setLineItemValue(lineItem, 'yield', parseFloat(data.initial_yield).toFixed(3))
    }

    if (data.initial_margin !== undefined) {
      this.setLineItemValue(lineItem, 'margin', parseFloat(data.initial_margin).toFixed(3))

      // Store the initial margin as the base for spread calculations
      // This is the recalculated margin at inventory price, not the raw inventory margin
      lineItem.dataset.initialMargin = (parseFloat(data.initial_margin) / 100).toFixed(6)
      console.log('Stored initial margin for spread calculations:', lineItem.dataset.initialMargin)

      // Mark that we've set the initial margin from inventory
      if (data.inventory && data.inventory.inventory_margin !== undefined) {
        lineItem.dataset.hasInventoryMargin = 'true'
        // Store the precise inventory margin value to avoid rounding issues
        lineItem.dataset.preciseInventoryMargin = data.inventory.inventory_margin
        lineItem.dataset.marginManuallyChanged = 'false'
        console.log('Stored precise inventory margin from initial values:', data.inventory.inventory_margin)
      }

      // Store the precise inventory yield value to avoid recalculation drift
      if (data.inventory && data.inventory.inventory_yield !== undefined) {
        lineItem.dataset.preciseInventoryYield = data.inventory.inventory_yield
        console.log('Stored precise inventory yield from initial values:', data.inventory.inventory_yield)
      }

      // Calculate initial spread (pass margin as percentage)
      this.calculateInitialSpread(lineItem, data.initial_margin)
    }
  }

  calculateInitialSpread(lineItem, quotedMargin) {
    const inventoryMargin = this.getLineItemNumericValue(lineItem, 'inventory_margin', null)
    const marketMargin = this.getLineItemNumericValue(lineItem, 'market_margin', null)
    const spreadField = this.getLineItemField(lineItem, 'spread')
    const transactionType = this.getLineItemValue(lineItem, 'transaction_type') || TRANSACTION_TYPES.BUY

    // When we have inventory, the initial spread should be 0
    // because we're quoting at the inventory price (even if it's recalculated)
    if (inventoryMargin !== null && !isNaN(inventoryMargin) && spreadField) {
      spreadField.value = '0.000'
      console.log('Setting initial spread to 0 for inventory-based pricing')
    } else if (marketMargin !== null && !isNaN(marketMargin) && spreadField) {
      // Only calculate spread when using market margin (no inventory)
      const baseMarginPercentage = marketMargin * 100

      const spread = transactionType === TRANSACTION_TYPES.BUY
        ? quotedMargin - baseMarginPercentage
        : baseMarginPercentage - quotedMargin
      spreadField.value = spread.toFixed(3)
      console.log(`Calculated initial spread: ${spread.toFixed(3)}% (quotedMargin=${quotedMargin}%, marketMargin=${baseMarginPercentage}%)`)
    } else if (spreadField) {
      spreadField.value = ''
      console.log('No inventory or market margin available, leaving spread empty')
    }
  }

  storeInventoryData(lineItem, inventory) {
    if (!inventory) return

    const lineItemIndex = this.extractLineItemIndex(lineItem)
    if (!lineItemIndex) return

    // Inventory margin is already in decimal format from backend
    // When auto-recalculation happens, the inventory margin should be the recalculated value
    // to match old system behavior (ensuring spread = 0)

    const inventoryMarginToStore = inventory.inventory_margin !== undefined ? inventory.inventory_margin : ''

    // Create or update inventory fields
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_margin', inventoryMarginToStore)
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_yield', inventory.inventory_yield || '')
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_price', inventory.inventory_price || '')
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_fetched_at', new Date().toISOString())

    // Store the precise margin and yield values for calculations to avoid rounding issues
    if (inventory.inventory_margin !== undefined) {
      lineItem.dataset.preciseInventoryMargin = inventory.inventory_margin
      // Mark that margin hasn't been manually changed yet
      lineItem.dataset.marginManuallyChanged = 'false'
      console.log('Stored precise inventory margin:', inventory.inventory_margin)
    }

    if (inventory.inventory_yield !== undefined) {
      lineItem.dataset.preciseInventoryYield = inventory.inventory_yield
      console.log('Stored precise inventory yield:', inventory.inventory_yield)
    }

    // IMPORTANT: Also store the inventory price as a data attribute for easy access in premium calculations
    if (inventory.inventory_price) {
      lineItem.dataset.inventoryPrice = inventory.inventory_price
      // Also update initialCapitalPrice to use inventory price as the base for premium calculations
      lineItem.dataset.initialCapitalPrice = inventory.inventory_price
      console.log(`Stored inventory price as data attribute: ${inventory.inventory_price}`)
    }

    // Store available quantity as data attribute
    lineItem.dataset.inventoryAvailableQuantity = inventory.available_quantity || 0

    // Store minimum denomination as data attribute
    if (inventory.minimum_denomination) {
      lineItem.dataset.minimumDenomination = inventory.minimum_denomination
    }

    // Store minimum parcel size as data attribute
    if (inventory.minimum_parcel_size) {
      lineItem.dataset.minimumParcelSize = inventory.minimum_parcel_size
      // Don't auto-correct face value - let validation show and user correct manually
    }

    // Update the availability display
    this.updateInventoryAvailabilityDisplay(lineItem)

    // Update the inventory display to show margin and price
    this.updateInventoryDisplay(lineItem)
  }

  createOrUpdateHiddenField(lineItem, index, fieldName, value) {
    const name = `oms_quote[quote_line_items_attributes][${index}][${fieldName}]`
    let field = lineItem.querySelector(`input[name="${name}"]`)

    if (!field) {
      field = document.createElement('input')
      field.type = 'hidden'
      field.name = name
      field.value = value
      lineItem.appendChild(field)
    } else {
      field.value = value
    }
  }

  // ============== PRICING DISPLAY ==============
  async updatePricingDisplay(lineItem) {
    const data = this.extractLineItemData(lineItem)

    // For principal calculations:
    // - In capital-price-premium mode: use the displayed capital price (already includes premium)
    // - In margin-spread mode: use the price as-is
    const commissionType = this.element.dataset.commissionType || 'margin-spread'
    let calculationPrice = data.price

    // Principal should be calculated from the quoted price (capital price) which already includes premium
    // We don't need to apply premium again since it's already in the capital price
    if (commissionType === 'capital-price-premium') {
      console.log(`updatePricingDisplay using capital price for principal: ${calculationPrice} (already includes premium)`)
    }

    console.log(`updatePricingDisplay called: securityId=${data.securityId}, quantity=${data.quantity}, price=${calculationPrice}`)

    if (!data.securityId) {
      console.log('No security ID, skipping pricing display update')
      return
    }

    if (!data.quantity || data.quantity <= 0) {
      console.log('No valid quantity, clearing pricing displays')
      this.updateLineItemDisplay(lineItem, '.principal-display', '$0.00')
      // Only update extra info accrued interest, exclude market pricing section
      const extraInfoAccruedElements = lineItem.querySelectorAll('.accrued-interest-display:not(.market-pricing-section .accrued-interest-display)')
      extraInfoAccruedElements.forEach(element => {
        element.textContent = '$0.00'
      })
      this.updateLineItemDisplay(lineItem, '.total-consideration-display', '$0.00')
      return
    }

    if (!calculationPrice || calculationPrice <= 0) {
      console.log('No valid price, clearing pricing displays')
      this.updateLineItemDisplay(lineItem, '.principal-display', '$0.00')
      // Only update extra info accrued interest, exclude market pricing section
      const extraInfoAccruedElements = lineItem.querySelectorAll('.accrued-interest-display:not(.market-pricing-section .accrued-interest-display)')
      extraInfoAccruedElements.forEach(element => {
        element.textContent = '$0.00'
      })
      this.updateLineItemDisplay(lineItem, '.total-consideration-display', '$0.00')
      return
    }


    // Calculate and display principal
    // In capital-price-premium mode, the displayed price already includes premium, so don't apply it again
    let adjustedPrice
    if (commissionType === 'capital-price-premium') {
      // Capital price already includes the premium, use it directly
      adjustedPrice = calculationPrice
    } else {
      // In margin-spread mode, apply the spread to get the adjusted price
      const commissionValue = data.spread
      adjustedPrice = PricingCalculations.calculateSpreadAdjustedPrice(
        calculationPrice,
        commissionValue,
        data.transactionType,
        commissionType
      )
    }

    const principal = PricingCalculations.calculatePrincipal(
      data.quantity,
      adjustedPrice,
      data.parValuePricing
    )

    this.updateLineItemDisplay(lineItem, '.principal-display', formatCurrency(principal))

    // Fetch and display accrued interest
    if (data.quantity > 0 && data.price > 0) {
      try {
        console.log('Calculating accrued interest for:', {
          securityId: data.securityId,
          quantity: data.quantity,
          settlementDate: data.settlementDate || this.getQuoteSettlementDate()
        })

        const result = await this.pricingAPI.calculateAccruedInterest(
          data.securityId,
          data.quantity,
          data.settlementDate || this.getQuoteSettlementDate()
        )

        console.log('Accrued interest result:', result)

        const accruedAmount = parseFloat(result.accrued_interest) || 0
        // Only update extra info accrued interest, exclude market pricing section
        const extraInfoAccruedElements = lineItem.querySelectorAll('.accrued-interest-display:not(.market-pricing-section .accrued-interest-display)')
        extraInfoAccruedElements.forEach(element => {
          element.textContent = formatCurrency(accruedAmount)
        })
        // Update total consideration in BOTH locations (main row and market section)
        this.updateLineItemDisplay(lineItem, '.total-consideration-display', formatCurrency(principal + accruedAmount))
      } catch (error) {
        console.error('Failed to fetch accrued interest:', error)
        // Only update extra info accrued interest, exclude market pricing section
        const extraInfoAccruedElements = lineItem.querySelectorAll('.accrued-interest-display:not(.market-pricing-section .accrued-interest-display)')
        extraInfoAccruedElements.forEach(element => {
          element.textContent = '$0.00'
        })
        // Update total consideration in BOTH locations (main row and market section)
        this.updateLineItemDisplay(lineItem, '.total-consideration-display', formatCurrency(principal))
      }
    } else {
      // Only update extra info accrued interest, exclude market pricing section
      const extraInfoAccruedElements = lineItem.querySelectorAll('.accrued-interest-display:not(.market-pricing-section .accrued-interest-display)')
      extraInfoAccruedElements.forEach(element => {
        element.textContent = '$0.00'
      })
      // Update total consideration in BOTH locations (main row and market section)
      this.updateLineItemDisplay(lineItem, '.total-consideration-display', '$0.00')
    }
  }

  // ============== UI UPDATES ==============
  updateSecurityCountDisplay() {
    // Only update the security count, not the totals
    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    let validCount = 0

    lineItems.forEach(item => {
      const destroyField = item.querySelector('input[name*="_destroy"]')
      if (!destroyField || destroyField.value !== "1") {
        const securityId = item.querySelector('input[name*="[security_id]"]')?.value
        if (securityId) validCount++
      }
    })

    // Update count displays
    if (this.hasCountTarget) {
      this.countTarget.textContent = validCount
    }
    if (this.hasSidebarCountTarget) {
      this.sidebarCountTarget.textContent = validCount
    }
    if (this.hasSummaryCountTarget) {
      this.summaryCountTarget.textContent = validCount
    }
  }

  updateCounts() {
    const visibleItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    const totals = this.calculateTotals(visibleItems)

    this.updateCountDisplays(visibleItems.length, totals)
    this.updateSettlementDisplay()
  }

  // Update market pricing for a specific line item when face value changes
  updateMarketPricing(event) {
    const lineItem = event.target.closest('.quote-line-item')
    if (!lineItem) return

    const data = this.extractLineItemData(lineItem)
    console.log('updateMarketPricing called for line item:', {
      securityId: data.securityId,
      quantity: data.quantity
    })

    // Get the stored market pricing data and recalculate
    const marketPriceDisplay = lineItem.querySelector('.market-price-display')
    if (marketPriceDisplay) {
      const securityId = data.securityId
      if (securityId) {
        // Re-fetch and display market pricing to trigger the calculation with new quantity
        this.fetchMarketPricing(lineItem, securityId)
      }
    }
  }

  calculateTotals(visibleItems) {
    // Group by transaction type and currency
    const totals = {
      buy: {},
      sell: {}
    }

    visibleItems.forEach((item, index) => {
      this.updateSecurityNumber(item, index + 1)

      const data = this.extractLineItemData(item)
      const currency = item.dataset.currencyCode || 'AUD'

      // Skip items without valid security ID
      if (!data.securityId) {
        return
      }

      const commissionType = this.element.dataset.commissionType || 'margin-spread'
      const commissionValue = commissionType === 'capital-price-premium' ? data.premium : data.spread

      // Only calculate total value if we have valid price and quantity
      let totalValue = 0
      if (data.price !== null && data.price !== undefined && !isNaN(data.price) &&
          data.quantity !== null && data.quantity !== undefined && !isNaN(data.quantity) && data.quantity > 0) {
        const adjustedPrice = PricingCalculations.calculateSpreadAdjustedPrice(
          data.price,
          commissionValue,
          data.transactionType,
          commissionType
        )

        if (adjustedPrice !== null && adjustedPrice !== undefined && !isNaN(adjustedPrice)) {
          totalValue = PricingCalculations.calculatePrincipal(
            data.quantity,
            adjustedPrice,
            data.parValuePricing
          )
        }
      }

      if (data.transactionType === TRANSACTION_TYPES.BUY) {
        if (!totals.buy[currency]) {
          totals.buy[currency] = { count: 0, total: 0 }
        }
        totals.buy[currency].count++
        totals.buy[currency].total += totalValue || 0
      } else {
        if (!totals.sell[currency]) {
          totals.sell[currency] = { count: 0, total: 0 }
        }
        totals.sell[currency].count++
        totals.sell[currency].total += totalValue || 0
      }
    })

    return totals
  }

  updateCountDisplays(count, totals) {
    // Update count targets
    if (this.hasCountTarget) this.countTarget.textContent = count
    if (this.hasSidebarCountTarget) this.sidebarCountTarget.textContent = count
    if (this.hasSummaryCountTarget) this.summaryCountTarget.textContent = count

    // Update popover targets (may be outside controller scope, so search globally)
    const popoverCount = document.querySelector('[data-oms--quote-securities-target="popoverCount"]')
    if (popoverCount) popoverCount.textContent = count

    const popoverSummaryCount = document.querySelector('[data-oms--quote-securities-target="popoverSummaryCount"]')
    if (popoverSummaryCount) popoverSummaryCount.textContent = count

    // Build transaction summary rows
    const buildTransactionSummaryContent = (targetElement) => {
      targetElement.innerHTML = ''

      // Get all currencies and sort them
      const allCurrencies = [...new Set([
        ...Object.keys(totals.buy),
        ...Object.keys(totals.sell)
      ])].sort()

      // Create rows for each currency/transaction type combination
      allCurrencies.forEach(currency => {
        // Buy row for this currency
        if (totals.buy[currency]) {
          const buyData = totals.buy[currency]
          const buyRow = document.createElement('div')
          buyRow.className = 'flex justify-between'
          buyRow.innerHTML = `
            <span class="text-neutral-600">Buy (${buyData.count}) Securities (${currency})</span>
            <span>${formatCurrency(buyData.total)}</span>
          `
          targetElement.appendChild(buyRow)
        }

        // Sell row for this currency
        if (totals.sell[currency]) {
          const sellData = totals.sell[currency]
          const sellRow = document.createElement('div')
          sellRow.className = 'flex justify-between'
          sellRow.innerHTML = `
            <span class="text-neutral-600">Sell (${sellData.count}) Securities (${currency})</span>
            <span>${formatCurrency(sellData.total)}</span>
          `
          targetElement.appendChild(sellRow)
        }
      })

      // If no transactions, show default rows
      if (allCurrencies.length === 0) {
        targetElement.innerHTML = `
          <div class="flex justify-between">
            <span class="text-neutral-600">Buy (0) Securities (AUD)</span>
            <span>$0.00</span>
          </div>
          <div class="flex justify-between">
            <span class="text-neutral-600">Sell (0) Securities (AUD)</span>
            <span>$0.00</span>
          </div>
        `
      }
    }

    if (this.hasTransactionSummaryTarget) {
      buildTransactionSummaryContent(this.transactionSummaryTarget)
    }

    // Update popover transaction summary (may be outside controller scope)
    const popoverTransactionSummary = document.querySelector('[data-oms--quote-securities-target="popoverTransactionSummary"]')
    if (popoverTransactionSummary) {
      buildTransactionSummaryContent(popoverTransactionSummary)
    }

    // Build net value summary rows
    const buildNetValueSummaryContent = (targetElement) => {
      targetElement.innerHTML = ''

      // Calculate net values by currency
      const netValuesByCurrency = {}
      const allCurrencies = [...new Set([
        ...Object.keys(totals.buy),
        ...Object.keys(totals.sell)
      ])].sort()

      allCurrencies.forEach(currency => {
        const buyTotal = totals.buy[currency]?.total || 0
        const sellTotal = totals.sell[currency]?.total || 0
        const netValue = buyTotal - sellTotal

        if (netValue !== 0 || (buyTotal === 0 && sellTotal === 0 && totals.buy[currency])) {
          netValuesByCurrency[currency] = netValue
        }
      })

      // Create net value rows
      Object.entries(netValuesByCurrency).forEach(([currency, netValue]) => {
        const netRow = document.createElement('div')
        netRow.className = 'flex justify-between font-medium'
        netRow.innerHTML = `
          <span>Net Value (${currency})</span>
          <span>${formatCurrency(netValue)}</span>
        `
        targetElement.appendChild(netRow)
      })

      // If no net values, show default AUD row
      if (Object.keys(netValuesByCurrency).length === 0) {
        targetElement.innerHTML = `
          <div class="flex justify-between font-medium">
            <span>Net Value (AUD)</span>
            <span>$0.00</span>
          </div>
        `
      }
    }

    if (this.hasNetValueSummaryTarget) {
      buildNetValueSummaryContent(this.netValueSummaryTarget)
    }

    // Update popover net value summary (may be outside controller scope)
    const popoverNetValueSummary = document.querySelector('[data-oms--quote-securities-target="popoverNetValueSummary"]')
    if (popoverNetValueSummary) {
      buildNetValueSummaryContent(popoverNetValueSummary)
    }
  }

  updateLineItemCalculations(lineItem) {
    const data = this.extractLineItemData(lineItem)

    // Always validate using the current input value, not extracted data
    const spreadInput = lineItem.querySelector('.spread-input')
    if (spreadInput) {
      const currentSpreadValue = parseFloat(spreadInput.value) || 0
      this.validateSpread(lineItem, currentSpreadValue)
    }

    // Calculate gross consideration
    const grossDisplay = lineItem.querySelector('.gross-consideration-display')
    if (grossDisplay) {
      if (data.price > 0 && data.quantity > 0) {
        const commissionType = this.element.dataset.commissionType || 'margin-spread'
        const commissionValue = commissionType === 'capital-price-premium' ? data.premium : data.spread

        const adjustedPrice = PricingCalculations.calculateSpreadAdjustedPrice(
          data.price,
          commissionValue,
          data.transactionType,
          commissionType
        )
        const grossConsideration = PricingCalculations.calculatePrincipal(
          data.quantity,
          adjustedPrice,
          data.parValuePricing
        )
        grossDisplay.textContent = formatCurrency(grossConsideration)
      } else {
        grossDisplay.textContent = '$0.00'
      }
    }
  }

  updateAllLineItemCalculations() {
    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    lineItems.forEach(item => this.updateLineItemCalculations(item))
  }

  async updatePricingForAllLineItems() {
    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')

    // Convert NodeList to array and filter out items without securities
    const lineItemsArray = Array.from(lineItems).filter(lineItem => {
      const securityId = this.getLineItemValue(lineItem, 'security_id')
      return securityId && securityId.trim() !== ''
    })

    if (lineItemsArray.length === 0) {
      console.log('No line items with securities found, skipping pricing updates')
      return
    }

    // Clear zero-spread price cache for all non-inventory line items
    // Settlement date change affects pricing, so we need to recalculate
    lineItemsArray.forEach(lineItem => {
      const hasInventory = lineItem.querySelector('input[name*="[inventory_price]"]')?.value
      if (!hasInventory && lineItem.dataset.zeroSpreadPrice) {
        delete lineItem.dataset.zeroSpreadPrice
        console.log('Cleared zero-spread price due to quote settlement date change')
      }
    })

    // Update pricing calculations and displays for all line items
    const updatePromises = lineItemsArray.map(async (lineItem) => {
      const securityId = this.getLineItemValue(lineItem, 'security_id')
      // Recalculate pricing based on new settlement date
      await this.calculateLineItemPricing(lineItem)
      // Update pricing display (includes accrued interest calculation)
      this.updatePricingDisplay(lineItem)
    })

    // Wait for all pricing updates to complete
    await Promise.all(updatePromises)

    // Update all line item calculations and counts
    this.updateAllLineItemCalculations()
    this.updateCounts()
  }

  // ============== FIELD INPUT HANDLERS ==============
  onPriceFieldInput(event) {
    const lineItem = event.target.closest('.quote-line-item')
    const fieldType = event.target.dataset.fieldType

    console.log(`onPriceFieldInput triggered - fieldType: ${fieldType}`)

    // Mark field as user-changed for tracking manual edits
    event.target.dataset.userChanged = 'true'

    // IMPORTANT: In capital-price-premium mode, update premium on EVERY keystroke in price field
    // This must happen BEFORE the clearing logic so it runs every time
    if (fieldType === 'price') {
      const commissionType = this.element.dataset.commissionType || 'margin-spread'

      if (commissionType === 'capital-price-premium' && !event.target.dataset.programmaticUpdate) {
        const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
        const premiumInput = lineItem.querySelector('.premium-input')

        let baselinePrice = 0
        let baselineSource = 'unknown'

        // Get baseline price for premium calculation
        // Priority: inventory price > zero-spread price > initial capital price (fallback)
        if (inventoryPriceInput && inventoryPriceInput.value) {
          baselinePrice = parseFloat(inventoryPriceInput.value)
          baselineSource = 'inventory_input'
        } else if (lineItem.dataset.inventoryPrice) {
          baselinePrice = parseFloat(lineItem.dataset.inventoryPrice)
          baselineSource = 'inventory_dataset'
        }
        // For non-inventory securities, use zero-spread price
        else if (lineItem.dataset.zeroSpreadPrice) {
          baselinePrice = parseFloat(lineItem.dataset.zeroSpreadPrice)
          baselineSource = 'zero_spread_price'
        }
        // Fallback to initial price/capital price
        else if (lineItem.dataset.initialPrice) {
          baselinePrice = parseFloat(lineItem.dataset.initialPrice)
          baselineSource = 'initial_price'
        } else if (lineItem.dataset.initialCapitalPrice) {
          baselinePrice = parseFloat(lineItem.dataset.initialCapitalPrice)
          baselineSource = 'initial_capital_price'
        }

        if (premiumInput && baselinePrice > 0) {
          const capitalPrice = parseFloat(event.target.value) || 0

          if (capitalPrice > 0) {
            const transactionType = lineItem.querySelector('select[name*="[transaction_type]"]')?.value || 'buy'
            let calculatedPremium

            if (transactionType === 'buy') {
              calculatedPremium = capitalPrice - baselinePrice
            } else {
              calculatedPremium = baselinePrice - capitalPrice
            }

            console.log('UPDATING PREMIUM from capital price change:', {
              capitalPrice,
              baselinePrice,
              baselineSource,
              transactionType,
              calculatedPremium
            })

            // Update premium field
            premiumInput.dataset.programmaticUpdate = 'true'
            premiumInput.value = calculatedPremium.toFixed(3)
            delete premiumInput.dataset.programmaticUpdate

            // Validate the premium
            this.validatePremium(lineItem, calculatedPremium)

            // Mark that premium was updated from price
            event.target.dataset.premiumUpdatedFromPrice = 'true'
          }
        }
      }
    }

    // IMPORTANT: Update spread on EVERY keystroke in margin field
    // This must happen BEFORE the clearing logic so it runs every time
    if (fieldType === 'margin' && !event.target.dataset.programmaticUpdate) {
      // CRITICAL: Set the flag BEFORE calling calculateAndUpdateSpreadFromMargin
      // Otherwise extractLineItemData will use the precise margin instead of display margin
      lineItem.dataset.marginManuallyChanged = 'true'
      this.calculateAndUpdateSpreadFromMargin(lineItem, true) // force=true to bypass skip logic
    }

    // Only clear once when user starts typing
    if (!event.target.dataset.cleared) {
      console.log(`User started typing in ${fieldType}, clearing related fields`)

      this.clearRelatedFields(lineItem, fieldType)

      // Special handling for margin and spread
      if (fieldType === 'margin') {
        // Flag is already set above for spread calculation
        console.log('Margin manually changed by user - will use display value')

        // Mark spread as needing recalculation when margin changes manually
        const spreadInput = lineItem.querySelector('.spread-input')
        if (spreadInput) {
          spreadInput.dataset.userChanged = 'true'
        }

        // In capital-price-premium mode, clear premium when user changes margin
        // User is now controlling via margin, not premium - can't have both
        const commissionType = this.element.dataset.commissionType || 'margin-spread'
        if (commissionType === 'capital-price-premium') {
          const premiumInput = lineItem.querySelector('.premium-input')
          if (premiumInput && premiumInput.value) {
            console.log('Clearing premium since user is now controlling via margin')
            premiumInput.value = '0.000'
          }
        }
      } else if (fieldType === 'spread') {
        // Mark that margin will be calculated from spread
        lineItem.dataset.marginManuallyChanged = 'true'
        console.log('Spread changed - margin will be calculated, marking as changed')

        // For spread, only clear the related fields on input
        // Don't calculate margin yet - wait for blur event
        // This prevents interference while the user is typing
        console.log('User typing in spread field - clearing margin/price/yield only')
        // Validate spread immediately to show/hide warnings
        const spreadValue = parseFloat(event.target.value) || 0
        this.validateSpread(lineItem, spreadValue)
      } else if (fieldType === 'premium') {
        // Mark that margin may be affected by premium
        lineItem.dataset.marginManuallyChanged = 'true'
        console.log('Premium changed - margin may be affected, marking as changed')
        // For premium, validate immediately to show/hide warnings
        console.log('User typing in premium field')
        const premiumValue = parseFloat(event.target.value) || 0
        this.validatePremium(lineItem, premiumValue)
      }

      event.target.dataset.cleared = 'true'
    } else if (fieldType === 'spread') {
      // Continue validating spread on every input
      const spreadValue = parseFloat(event.target.value) || 0
      this.validateSpread(lineItem, spreadValue)
    } else if (fieldType === 'premium') {
      // Continue validating premium on every input
      const premiumValue = parseFloat(event.target.value) || 0
      this.validatePremium(lineItem, premiumValue)
    }
    // Note: Premium update for price field is handled at the top of this function (lines 2547-2602)
  }

  async onPriceFieldBlur(event) {
    event.target.removeAttribute('data-cleared')

    const lineItem = event.target.closest('.quote-line-item')
    const fieldType = event.target.dataset.fieldType

    if (lineItem) {
      // Special handling for spread field
      if (fieldType === 'spread') {
        // Only recalculate if spread was actually changed
        if (event.target.dataset.userChanged === 'true') {
          console.log('Spread field blur - calculating margin from spread')

          // Mark that spread was manually changed so we know to update premium after calculation
          lineItem.dataset.spreadManuallyChanged = 'true'

          // Clear premium flag - spread and premium are mutually exclusive as user input sources
          delete lineItem.dataset.premiumManuallyChanged

          await this.calculateAndUpdateMarginFromSpread(lineItem)

          // Clear the userChanged flag
          delete event.target.dataset.userChanged

          // Validate the spread value to show/hide warnings
          const spreadValue = parseFloat(event.target.value) || 0
          this.validateSpread(lineItem, spreadValue)
        } else {
          console.log('Spread field blur without changes - skipping recalculation')
        }
      } else if (fieldType === 'premium') {
        // Only recalculate if premium was actually changed
        if (event.target.dataset.userChanged === 'true') {
          // DON'T clear the userChanged flag here - keep it set until user changes another field
          // The flag will be cleared in clearRelatedFields when user changes yield/margin/price
          console.log('Premium manually changed by user - calculating price and keeping userChanged flag')

          // Call the extracted method
          await this.calculateAndUpdatePriceFromPremium(lineItem)

          // IMPORTANT: For non-inventory securities, ensure we have a zero-spread price before calculating from premium
          // This ensures premium calculations have a proper baseline
          await this.ensureZeroSpreadPriceExists(lineItem)

          // Get the baseline price for premium calculation
          const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
          const priceInput = lineItem.querySelector('.price-input')
          const transactionType = lineItem.querySelector('select[name*="[transaction_type]"]')?.value || 'buy'

          // Get baseline price using priority: inventory > zero-spread > initial capital
          let baselinePrice = 0
          let baselineSource = 'unknown'

          // Priority 1: Check for actual inventory price
          if (inventoryPriceInput && inventoryPriceInput.value) {
            baselinePrice = parseFloat(inventoryPriceInput.value)
            baselineSource = 'inventory_input'
            console.log(`Found baseline price from hidden input: ${baselinePrice}`)
          }
          // Then try the data attribute we store when inventory is loaded
          else if (lineItem.dataset.inventoryPrice) {
            baselinePrice = parseFloat(lineItem.dataset.inventoryPrice)
            baselineSource = 'inventory_dataset'
            console.log(`Found baseline price from inventory data attribute: ${baselinePrice}`)
          }
          // Priority 2: For non-inventory securities, use zero-spread price
          else if (lineItem.dataset.zeroSpreadPrice) {
            baselinePrice = parseFloat(lineItem.dataset.zeroSpreadPrice)
            baselineSource = 'zero_spread_price'
            console.log(`Using zero-spread price as baseline (no inventory): ${baselinePrice}`)
          }
          // Priority 3: Fallback to initial price/capital price
          else if (lineItem.dataset.initialPrice) {
            baselinePrice = parseFloat(lineItem.dataset.initialPrice)
            baselineSource = 'initial_price'
            console.log(`Found baseline price from initial price data attribute: ${baselinePrice}`)
          }
          // Try the initial capital price we stored on load (least preferred)
          else if (lineItem.dataset.initialCapitalPrice) {
            baselinePrice = parseFloat(lineItem.dataset.initialCapitalPrice)
            baselineSource = 'initial_capital_price'
            console.log(`WARNING: Using initial capital price as baseline (may be inaccurate): ${baselinePrice}`)
          }
          // If still no baseline price, try to extract it from the current state
          else if (priceInput && priceInput.value) {
            // If we don't have a baseline price stored, use the current capital price
            // This is a last resort fallback
            const currentPrice = parseFloat(priceInput.value)
            baselinePrice = currentPrice
            baselineSource = 'current_price'
            console.log(`Using current price as baseline (last resort fallback): ${baselinePrice}`)
          }
          else {
            console.log('WARNING: Could not find any baseline price for premium calculation')
          }

          if (baselinePrice > 0 && priceInput) {
            // Get the premium value
            const premiumInput = lineItem.querySelector('.premium-input')
            const premiumValue = premiumInput ? parseFloat(premiumInput.value) || 0 : 0

            // Calculate the new capital price based on baseline + premium
            let newCapitalPrice
            if (transactionType === 'buy') {
              newCapitalPrice = baselinePrice + premiumValue
            } else {
              newCapitalPrice = baselinePrice - premiumValue
            }

            console.log('Setting capital price from premium:', {
              baselinePrice,
              baselineSource,
              premium: premiumValue,
              transactionType,
              newCapitalPrice
            })

            // Update the price field with the new capital price (inventory + premium)
            priceInput.value = newCapitalPrice.toFixed(3)
            priceInput.dataset.priceSetFromPremium = 'true'
            priceInput.dataset.skipPremium = 'true'  // Don't send premium to backend to avoid double application
            console.log('Set capital price to inventory + premium:', newCapitalPrice)

            // Clear yield and margin fields since price has changed
            const yieldInput = lineItem.querySelector('.yield-input')
            const marginInput = lineItem.querySelector('.margin-input')
            if (yieldInput) {
              yieldInput.value = ''
              yieldInput.dataset.cleared = 'true'
            }
            if (marginInput) {
              marginInput.value = ''
              marginInput.dataset.cleared = 'true'
            }

            // Mark that we should skip sending premium to backend (it's already in the price)
            priceInput.dataset.skipPremium = 'true'

            console.log('Updated capital price from premium - will calculate with adjusted price')

            // Trigger pricing calculation with the adjusted price
            await this.calculateLineItemPricing(lineItem)
            this.updatePricingDisplay(lineItem)
            this.updateCounts()

            // Clean up the flag
            delete priceInput.dataset.skipPremium
            return  // Exit to avoid duplicate calculation below
          } else {
            console.log('Cannot update capital price from premium - missing inventory price')

            // Clear price and yield to force backend to recalculate from margin
            const yieldInput = lineItem.querySelector('.yield-input')
            if (priceInput) {
              priceInput.value = ''
              priceInput.dataset.cleared = 'true'
            }
            if (yieldInput) {
              yieldInput.value = ''
              yieldInput.dataset.cleared = 'true'
            }

            console.log('Cleared price and yield to force recalculation from margin + premium')

            // Trigger pricing recalculation
            await this.calculateLineItemPricing(lineItem)
          }

          // After calculation, update margin display to show the effective margin with premium
          // The backend might return the base margin, but we need to show the adjusted margin
          const marginInput = lineItem.querySelector('.margin-input')
          const inventoryMarginInput = lineItem.querySelector('input[name*="[inventory_margin]"]')

          if (marginInput && inventoryMarginInput) {
            // Get the base margin (inventory margin)
            const baseMargin = parseFloat(inventoryMarginInput.value) || 0

            // Calculate the effective margin after premium adjustment
            // Premium affects the capital price, which affects the effective margin
            // For a simplified approach, we'll keep showing the base margin but could calculate the effective margin here
            // For now, ensure the margin field shows a value (it was being left blank)
            if (!marginInput.value || marginInput.value === '') {
              marginInput.value = (baseMargin * 100).toFixed(3)
              console.log('Updated margin display after premium change:', marginInput.value)
            }
          }

          this.updatePricingDisplay(lineItem)
          this.updateCounts()
        } else {
          console.log('Premium field blur without changes - skipping recalculation')
        }
      } else if (fieldType === 'price') {
        // Special handling for price field in capital-price-premium mode
        const commissionType = this.element.dataset.commissionType || 'margin-spread'

        // Check if we just updated premium from this price change
        if (event.target.dataset.premiumUpdatedFromPrice === 'true') {
          console.log('Price was manually changed and premium updated - calculating without sending premium')
          // Clear the flag for next time
          delete event.target.dataset.premiumUpdatedFromPrice

          // Mark that we should NOT send premium to backend
          event.target.dataset.skipPremium = 'true'

          // Calculate pricing to get yield/margin
          await this.calculateLineItemPricing(lineItem)
          this.updatePricingDisplay(lineItem)
          this.updateCounts()

          // Clean up the flag
          delete event.target.dataset.skipPremium
        } else if (event.target.dataset.priceSetFromPremium === 'true') {
          console.log('Price was set from premium - already calculated in premium handler')
          // Clear the flag for next time
          delete event.target.dataset.priceSetFromPremium

          // Already calculated in the premium field handler, just update display
          this.updatePricingDisplay(lineItem)
          this.updateCounts()
        } else {
          // Only recalculate if price actually changed
          // This prevents unwanted recalculations when tabbing through fields without editing
          if (event.target.dataset.userChanged === 'true') {
            console.log('Price manually changed by user - will calculate')

            // For manual price changes, mark that price was user-changed
            // This will prevent premium from being sent to backend (avoiding double application)
            const priceInput = event.target
            if (priceInput.dataset.userChanged === 'true') {
              console.log('Will calculate without sending premium')
            }

            // Recalculate pricing
            await this.calculateLineItemPricing(lineItem)
            this.updatePricingDisplay(lineItem)
            this.updateCounts()

            // Clear the userChanged flag after processing
            delete priceInput.dataset.userChanged
            console.log('Cleared userChanged flag after processing')
          } else {
            console.log('Price field blur without changes - skipping recalculation')
          }
        }
      } else {
        // All other field types (yield, margin) - only recalculate if actually changed
        // This prevents unwanted recalculations when tabbing through fields without editing
        if (event.target.dataset.userChanged === 'true') {
          console.log(`${fieldType} field manually changed by user - will calculate`)

          // IMPORTANT: When margin is manually changed, clear initialMargin
          // User's entered margin value is always sent to backend (correct!)
          // But after backend responds, we calculate spread for display: spread = inventory_margin - entered_margin
          // By clearing initialMargin, we ensure spread is relative to inventory_margin (raw baseline)
          // NOT relative to the "precise" initialMargin from Reset button
          if (fieldType === 'margin' && lineItem.dataset.initialMargin) {
            console.log('Clearing initialMargin - spread display will be relative to inventory_margin, not precise reset value')
            delete lineItem.dataset.initialMargin
          }

          await this.calculateLineItemPricing(lineItem)
          this.updatePricingDisplay(lineItem)
          this.updateCounts()

          // Clear the userChanged flag after processing
          delete event.target.dataset.userChanged
          console.log(`Cleared userChanged flag for ${fieldType} field after processing`)
        } else {
          console.log(`${fieldType} field blur without changes - skipping recalculation`)
        }
      }
    }
  }

  // ============== CUSTOM NAME HANDLING ==============
  toggleCustomNameField(event) {
    if (!this.hasCustomNameFieldTarget) return

    const isChecked = event.target.checked

    if (isChecked) {
      this.customNameFieldTarget.classList.remove('hidden')
      // Focus the name input when revealed
      const nameInput = this.customNameFieldTarget.querySelector('input[type="text"]')
      if (nameInput) {
        nameInput.focus()
      }
    } else {
      this.customNameFieldTarget.classList.add('hidden')
      // Clear the name field when hiding
      const nameInput = this.customNameFieldTarget.querySelector('input[type="text"]')
      if (nameInput) {
        nameInput.value = ''
      }
    }
  }

  // ============== SETTLEMENT HANDLING ==============
  updateSettlementDisplay() {
    const settlementType = this.hasSettlementTypeTarget ? this.settlementTypeTarget.value : null
    if (!settlementType) return

    // Show/hide settlement date field
    if (this.hasSettlementDateFieldTarget) {
      if (settlementType === 'negotiated') {
        this.settlementDateFieldTarget.classList.remove('hidden')
      } else {
        this.settlementDateFieldTarget.classList.add('hidden')
      }

      if (settlementType === 'negotiated' && this.hasSettlementDateInputTarget && !this.settlementDateInputTarget.value) {
        // Set default to tomorrow
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        this.settlementDateInputTarget.value = this.formatDateForInput(tomorrow)
      }
    }

    // Update sidebar display
    const displayText = this.getSettlementDisplayText(settlementType)
    if (this.hasSettlementDisplayTarget) {
      this.settlementDisplayTarget.textContent = displayText
    }

    // Update popover settlement display (may be outside controller scope)
    const popoverSettlementDisplay = document.querySelector('[data-oms--quote-securities-target="popoverSettlementDisplay"]')
    if (popoverSettlementDisplay) {
      popoverSettlementDisplay.textContent = displayText
    }
  }

  updateQuoteSettlementDisplays() {
    const settlementType = this.hasSettlementTypeTarget ? this.settlementTypeTarget.value : null
    const settlementDate = this.hasSettlementDateInputTarget ? this.settlementDateInputTarget.value : null

    const displayText = this.getSettlementDisplayText(settlementType, settlementDate)

    // Update all quote settlement displays
    const elements = this.element.querySelectorAll('.settlement-quote-default')
    elements.forEach(element => {
      element.textContent = displayText
    })

    // Adjust expiry date if it's now after the settlement date
    this.adjustExpiryDateForSettlementChange()
  }

  getSettlementDisplayText(settlementType, settlementDate = null) {
    if (settlementType === 'negotiated' && settlementDate) {
      return formatDate(settlementDate)
    }
    return SETTLEMENT_DISPLAY[settlementType] || 'T+2'
  }

  handleQuoteTypeChange(event) {
    const newQuoteType = event.target.value
    console.log('Quote type changed to:', newQuoteType)

    // Update the security search component with the new quote type
    if (this.hasSecuritySearchContainerTarget) {
      const searchController = this.securitySearchContainerTarget.querySelector('[data-controller*="security-search"]')
      if (searchController) {
        // Update the data attribute
        searchController.setAttribute('data-securities--security-search-quote-type-value', newQuoteType)
      }
    }

    // If changing to grey market, update settlement handling
    if (newQuoteType === 'grey_market') {
      // Grey market quotes always use negotiated settlement
      if (this.hasSettlementTypeTarget) {
        this.settlementTypeTarget.value = 'negotiated'
        this.updateSettlementDisplay()
      }
    }

    // Check if we need to clear incompatible securities
    if (newQuoteType === 'grey_market' && this.hasLineItemsTarget) {
      const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
      if (lineItems.length > 0) {
        if (confirm('Changing to Grey Market quote type will clear existing securities. Continue?')) {
          // Clear all line items
          lineItems.forEach(item => item.remove())
          this.updateCounts()
        } else {
          // Revert the change
          event.target.value = event.target.dataset.previousValue || 'indicative'
        }
      }
    }

    // Store the current value for future reference
    event.target.dataset.previousValue = newQuoteType
  }

  initializeSettlementOverrides() {
    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item')
    lineItems.forEach(lineItem => this.initializeLineItemSettlement(lineItem))
  }

  initializeLineItemSettlement(lineItem) {
    const checkbox = lineItem.querySelector('.settlement-override-checkbox')
    const settlementDateField = lineItem.querySelector('input[name*="[settlement_date]"]')

    if (checkbox && settlementDateField) {
      checkbox.checked = !!settlementDateField.value
      this.updateLineItemSettlementDisplay(lineItem)

      // Set the quote default display
      const displayText = this.getSettlementDisplayText(
        this.hasSettlementTypeTarget ? this.settlementTypeTarget.value : null,
        this.hasSettlementDateInputTarget ? this.settlementDateInputTarget.value : null
      )
      this.updateLineItemDisplay(lineItem, '.settlement-quote-default', displayText)
    }
  }

  async toggleSettlementOverride(event) {
    const checkbox = event.target
    const lineItem = checkbox.closest('.quote-line-item')
    const overrideFields = lineItem.querySelector('.settlement-override-fields')
    const defaultDisplay = lineItem.querySelector('.settlement-default-display')
    const settlementDateField = lineItem.querySelector('input[name*="[settlement_date]"]')

    if (checkbox.checked) {
      this.toggleLineItemElement(lineItem, '.settlement-override-fields', true)
      this.toggleLineItemElement(lineItem, '.settlement-default-display', false)

      if (settlementDateField && !settlementDateField.value) {
        // Set default to tomorrow
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        settlementDateField.value = this.formatDateForInput(tomorrow)
        settlementDateField.focus()
      }

      // Clear zero-spread price when settlement date changes
      // It needs to be recalculated for the new settlement date
      const hasInventory = lineItem.querySelector('input[name*="[inventory_price]"]')?.value
      if (!hasInventory && lineItem.dataset.zeroSpreadPrice) {
        delete lineItem.dataset.zeroSpreadPrice
        console.log('Cleared zero-spread price due to settlement override enabled')
      }
    } else {
      this.toggleLineItemElement(lineItem, '.settlement-override-fields', false)
      this.toggleLineItemElement(lineItem, '.settlement-default-display', true)

      if (settlementDateField) {
        settlementDateField.value = ''
      }

      // Clear zero-spread price when settlement date changes back to default
      // It needs to be recalculated for the default settlement date
      const hasInventory = lineItem.querySelector('input[name*="[inventory_price]"]')?.value
      if (!hasInventory && lineItem.dataset.zeroSpreadPrice) {
        delete lineItem.dataset.zeroSpreadPrice
        console.log('Cleared zero-spread price due to settlement override disabled')
      }

      this.recalculateAccruedInterestForLineItem(lineItem)
    }
  }

  async recalculateAccruedInterestForLineItem(lineItem) {
    const data = this.extractLineItemData(lineItem)
    if (!data.securityId || !data.quantity) return

    try {
      console.log('Recalculating accrued interest after settlement override deselection for:', {
        securityId: data.securityId,
        quantity: data.quantity,
        settlementDate: this.getQuoteSettlementDate()
      })

      const result = await this.pricingAPI.calculateAccruedInterest(
        data.securityId,
        data.quantity,
        this.getQuoteSettlementDate()
      )

      const accruedAmount = parseFloat(result.accrued_interest) || 0
      this.updateLineItemDisplay(lineItem, '.accrued-interest-display', formatCurrency(accruedAmount))
    } catch (error) {
      console.error('Failed to fetch accrued interest:', error)
      this.updateLineItemDisplay(lineItem, '.accrued-interest-display', '$0.00')
    }

    // Update accrued interest calculations since settlement date changed
    console.log('Settlement override toggled, updating accrued interest')
    this.updatePricingDisplay(lineItem)
  }

  updateLineItemSettlement(event) {
    const lineItem = event.target.closest('.quote-line-item')
    if (lineItem) {
      this.updateLineItemSettlementDisplay(lineItem)
    }
  }

  updateLineItemSettlementDisplay(lineItem) {
    const settlementDateField = lineItem.querySelector('input[name*="[settlement_date]"]')
    const checkbox = lineItem.querySelector('.settlement-override-checkbox')

    if (settlementDateField && settlementDateField.value) {
      checkbox.checked = true
      this.toggleLineItemElement(lineItem, '.settlement-override-fields', true)
      this.toggleLineItemElement(lineItem, '.settlement-default-display', false)
    } else {
      checkbox.checked = false
      this.toggleLineItemElement(lineItem, '.settlement-override-fields', false)
      this.toggleLineItemElement(lineItem, '.settlement-default-display', true)
    }
  }

  // ============== EXPIRY DATE HANDLING ==============
  updateExpiryDate(event) {
    const expiryDateInput = event.target
    const selectedDate = new Date(expiryDateInput.value + 'T00:00:00')

    // Convert to end of day for validation and storage
    const endOfDay = new Date(selectedDate)
    endOfDay.setHours(23, 59, 59, 999)

    // Validate the expiry date
    const validation = this.validateExpiryDate(endOfDay)

    if (!validation.valid) {
      // Show validation error
      this.showExpiryDateError(validation.message)
      // Reset to a valid default date
      this.resetExpiryDateToDefault()
    } else {
      // Clear any existing errors
      this.clearExpiryDateError()
    }
  }

  validateExpiryDate(expiryDateTime) {
    const today = new Date()
    today.setHours(23, 59, 59, 999) // End of today

    const settlementDate = this.getSettlementDate()

    // Rule 1: Expiry date must be > today
    if (expiryDateTime <= today) {
      return {
        valid: false,
        message: 'Quote expiry date must be after today'
      }
    }

    // Rule 2: Expiry date must be <= settlement date
    if (settlementDate && expiryDateTime > settlementDate.getTime()) {
      return {
        valid: false,
        message: 'Quote expiry date cannot be after settlement date'
      }
    }

    return { valid: true }
  }

  getSettlementDate() {
    const settlementType = this.hasSettlementTypeTarget ? this.settlementTypeTarget.value : null

    if (settlementType === 'negotiated' && this.hasSettlementDateInputTarget && this.settlementDateInputTarget.value) {
      const settlementDate = new Date(this.settlementDateInputTarget.value + 'T23:59:59')
      return settlementDate
    }

    // For T+ settlement types, calculate the settlement date
    const today = new Date()
    const businessDays = {
      't_plus_1': 1,
      't_plus_2': 2,
      't_plus_3': 3
    }

    const daysToAdd = businessDays[settlementType] || 2
    const settlementDate = this.addBusinessDays(today, daysToAdd)
    // Ensure settlement date is also at end of day for proper comparison
    settlementDate.setHours(23, 59, 59, 999)
    return settlementDate
  }

  addBusinessDays(date, days) {
    const result = new Date(date)
    let addedDays = 0

    while (addedDays < days) {
      result.setDate(result.getDate() + 1)
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        addedDays++
      }
    }

    // Don't set time here - let caller handle it
    return result
  }

  adjustExpiryDateForSettlementChange() {
    if (!this.hasExpiryDateInputTarget) return

    const currentExpiryDate = new Date(this.expiryDateInputTarget.value + 'T23:59:59')
    const settlementDate = this.getSettlementDate()

    // If expiry date is after settlement date, adjust it to match settlement date
    if (settlementDate && currentExpiryDate > settlementDate) {
      const adjustedDate = new Date(settlementDate)
      this.expiryDateInputTarget.value = this.formatDateForInput(adjustedDate)
      this.clearExpiryDateError()
    }
  }

  showExpiryDateError(message) {
    // Remove any existing error
    this.clearExpiryDateError()

    const input = this.expiryDateInputTarget
    const container = input.parentElement

    // Add error styling to input
    input.classList.add('border-error-500', 'focus:border-error-500', 'focus:ring-error-500')
    input.classList.remove('border-neutral-300', 'focus:border-info-500', 'focus:ring-info-500')

    // Add error message
    const errorDiv = document.createElement('div')
    errorDiv.className = 'expiry-date-error text-sm text-error-600 mt-1'
    errorDiv.textContent = message
    container.appendChild(errorDiv)
  }

  clearExpiryDateError() {
    if (!this.hasExpiryDateInputTarget) return

    const input = this.expiryDateInputTarget
    const container = input.parentElement

    // Remove error styling
    input.classList.remove('border-error-500', 'focus:border-error-500', 'focus:ring-error-500')
    input.classList.add('border-neutral-300', 'focus:border-info-500', 'focus:ring-info-500')

    // Remove error message
    const errorMessage = container.querySelector('.expiry-date-error')
    if (errorMessage) {
      errorMessage.remove()
    }
  }

  resetExpiryDateToDefault() {
    if (!this.hasExpiryDateInputTarget) return

    const settlementDate = this.getSettlementDate()
    if (settlementDate) {
      this.expiryDateInputTarget.value = this.formatDateForInput(settlementDate)
    }
  }

  formatDateForInput(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // ============== GLOBAL SPREAD ==============
  setupGlobalSpreadControls() {
    const button = this.element.querySelector('.global-spread-apply-button')
    const input = this.element.querySelector('.global-spread-input')

    if (button && input) {
      console.log("Setting up global spread controls")

      // Remove any existing Stimulus action to prevent double firing
      button.removeAttribute('data-action')
      button.addEventListener('click', async (event) => {
        event.preventDefault()
        await this.applyGlobalSpread(input)
      })

      input.addEventListener('keydown', async (event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          await this.applyGlobalSpread(input)
        }
      })
    } else {
      console.log("Global spread controls not found - this is OK if quote is not editable")
    }
  }

  async applyGlobalSpread(inputElement) {
    const spreadValue = parseFloat(inputElement.value)

    console.log("Applying global spread:", spreadValue)

    if (isNaN(spreadValue)) {
      console.warn("Invalid spread value")
      return
    }

    // Get spread limits
    const minSpread = parseFloat(this.element.dataset.spreadMin) || 0
    const maxSpread = parseFloat(this.element.dataset.spreadMax) || 100

    // Validate
    if (spreadValue < minSpread || spreadValue > maxSpread) {
      console.warn(`Spread ${spreadValue} is outside limits ${minSpread}-${maxSpread}`)
    }

    // Apply to all spread inputs and collect promises for pricing calculations
    const spreadInputs = this.lineItemsTarget.querySelectorAll('input[name*="[spread]"]')
    const pricingPromises = []

    spreadInputs.forEach((input) => {
      const lineItem = input.closest('.quote-line-item')
      if (lineItem) {
        // Set the spread value
        input.value = spreadValue.toFixed(3)

        // Mark that we should preserve this spread value during calculations
        input.dataset.preserve = 'true'

        // Calculate margin from spread (this will preserve the spread value)
        const promise = this.calculateAndUpdateMarginFromSpread(lineItem).then(() => {
          // Remove the preserve flag after calculation
          input.dataset.preserve = 'false'
        })
        pricingPromises.push(promise)
      }
    })

    // Wait for all pricing calculations to complete
    await Promise.all(pricingPromises)

    // Now update all calculations and counts
    this.updateAllLineItemCalculations()
    this.updateCounts()

    console.log("Global spread applied successfully")
  }

  // ============== MORE/LESS TOGGLE ==============
  toggleExtraInfo(event) {
    event.preventDefault()

    const button = event.currentTarget
    const lineItem = button.closest('.quote-line-item')
    const extraInfo = lineItem.querySelector('.extra-info')
    const textSpan = button.querySelector('.more-less-text')
    const icon = button.querySelector('svg')

    if (extraInfo.classList.contains('hidden')) {
      extraInfo.classList.remove('hidden')
      textSpan.textContent = 'Less'
      icon.style.transform = 'rotate(180deg)'

      // Update inventory display
      this.updateInventoryDisplay(lineItem)
    } else {
      extraInfo.classList.add('hidden')
      textSpan.textContent = 'More'
      icon.style.transform = 'rotate(0deg)'
    }
  }

  // ============== DRAG AND DROP ==============
  initializeSortable() {
    if (this.hasLineItemsTarget) {
      this.sortable = Sortable.create(this.lineItemsTarget, {
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        onEnd: (event) => {
          console.log('Item moved from index', event.oldIndex, 'to', event.newIndex)
          this.updateFormIndices()
          this.updateCounts()
        }
      })
    }
  }

  updateFormIndices() {
    // CRITICAL: Rails nested attributes handling
    // 1. Items with IDs (existing records) must keep their ORIGINAL indices
    // 2. Hidden items marked for destruction must keep ALL their fields intact
    // 3. Only NEW items (no ID) get fresh indices that don't conflict

    const allLineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item')
    const usedIndices = new Set()
    const indexMap = new Map() // Maps line items to their indices
    const securityIds = new Set() // Track security IDs for debugging

    // Debug: Log current state
    console.log('=== updateFormIndices called ===')
    console.log(`Total line items: ${allLineItems.length}`)

    // First pass: preserve indices for ALL items with IDs (visible or hidden)
    allLineItems.forEach((lineItem) => {
      const idField = lineItem.querySelector('input[name*="[id]"]')
      const securityIdField = lineItem.querySelector('input[name*="[security_id]"]')
      const destroyField = lineItem.querySelector('input[name*="[_destroy]"]')
      const isHidden = lineItem.style.display === 'none'

      if (securityIdField && securityIdField.value) {
        const secId = securityIdField.value
        if (securityIds.has(secId) && (!destroyField || destroyField.value !== "1")) {
          console.warn(`WARNING: Duplicate security_id ${secId} found in form!`)
        }
        securityIds.add(secId)
      }

      if (idField && idField.value) {
        // This is an existing record - preserve its current index
        const nameMatch = idField.name.match(/\[quote_line_items_attributes\]\[(\d+)\]/)
        if (nameMatch) {
          const index = parseInt(nameMatch[1])
          usedIndices.add(index)
          indexMap.set(lineItem, index)
          console.log(`Existing record: ID=${idField.value}, security_id=${securityIdField?.value}, index=${index}, hidden=${isHidden}, destroy=${destroyField?.value}`)
        }
      } else {
        console.log(`New record: security_id=${securityIdField?.value}, hidden=${isHidden}`)
      }
    })

    // Find the next safe starting index for new items
    let nextNewIndex = Math.max(0, ...Array.from(usedIndices), -1) + 1

    // Second pass: handle positions and new item indices
    let visiblePosition = 0

    allLineItems.forEach((lineItem) => {
      const isHidden = lineItem.style.display === 'none'
      const hasId = indexMap.has(lineItem)

      // Update position only for visible items
      if (!isHidden) {
        this.setLineItemValue(lineItem, 'position', visiblePosition)
        visiblePosition++
      }

      // Only assign new indices to items without IDs and that are visible
      if (!hasId && !isHidden) {
        // Find next available index
        while (usedIndices.has(nextNewIndex)) {
          nextNewIndex++
        }

        console.log(`Assigning index ${nextNewIndex} to new line item`)

        // Update all field names with the new index
        const fields = lineItem.querySelectorAll('input, select, textarea')
        fields.forEach(field => {
          if (field.name) {
            field.name = field.name.replace(
              /\[quote_line_items_attributes\]\[\w+\]/,
              `[quote_line_items_attributes][${nextNewIndex}]`
            )
          }
        })

        usedIndices.add(nextNewIndex)
        nextNewIndex++
      }
    })

    console.log('=== updateFormIndices complete ===')
  }


  // ============== CLEAR VALIDATION ERROR MESSAGES ==============
  clearValidationMessages(lineItem) {
    // Clear spread validation error message
    const spreadWarning = lineItem.querySelector('.spread-warning')
    if (spreadWarning) {
      spreadWarning.textContent = ''
      spreadWarning.classList.add('hidden')
    }

    // Clear premium validation error message
    const premiumWarning = lineItem.querySelector('.premium-warning')
    if (premiumWarning) {
      premiumWarning.textContent = ''
      premiumWarning.classList.add('hidden')
    }
  }

  // ============== REFRESH INVENTORY ==============
  async refreshInventory(event) {
    event.preventDefault()
    const button = event.currentTarget
    const lineItem = button.closest('.quote-line-item')
    const securityId = this.getLineItemValue(lineItem, 'security_id')

    if (!securityId) return

    // Show loading state
    const icon = button.querySelector('svg')
    if (icon) {
      icon.classList.add('animate-spin')
    }
    button.disabled = true

    try {
      const lineItemData = this.extractLineItemData(lineItem)
      const transactionType = lineItemData.transactionType

      // Store current spread value to preserve it
      const currentSpread = this.getLineItemNumericValue(lineItem, 'spread', 0)
      console.log('Preserving current spread:', currentSpread)

      const data = await this.pricingAPI.fetchMarketPricing(
        securityId,
        transactionType,
        this.getQuoteSettlementDate()
      )

      console.log('Refresh inventory response:', data)

      if (data.inventory?.inventory_margin !== null) {
        const currentMargin = this.getLineItemNumericValue(lineItem, 'inventory_margin', null)

        if (currentMargin !== null && parseFloat(currentMargin) !== parseFloat(data.inventory.inventory_margin)) {
          const confirmed = await this.showInventoryComparisonDialog(
            currentMargin,
            data.inventory.inventory_margin
          )

          if (confirmed) {
            await this.updateInventoryDataAndRecalculate(lineItem, data, currentSpread)
            this.resetSwapAndShortRatesFromAPI(lineItem, data)
            this.showRefreshMessage(lineItem, 'Inventory data updated and pricing recalculated', 'success')
          }
        } else {
          await this.updateInventoryDataAndRecalculate(lineItem, data, currentSpread)
          this.resetSwapAndShortRatesFromAPI(lineItem, data)
          this.showRefreshMessage(lineItem, 'Inventory data updated and pricing recalculated', 'success')
        }
      } else {
        this.showRefreshMessage(lineItem, 'No inventory data available', 'warning')
      }

      // Clear any validation error messages after refresh
      this.clearValidationMessages(lineItem)

    } catch (error) {
      console.error('Error refreshing inventory:', error)
      this.showRefreshMessage(lineItem, 'Failed to refresh inventory', 'error')
    } finally {
      const icon = button.querySelector('svg')
      if (icon) {
        icon.classList.remove('animate-spin')
      }
      button.disabled = false
    }
  }

  // ============== UPDATE TRANSACTION TYPE INDICATOR ==============
  updateTransactionTypeIndicator(event) {
    const select = event.currentTarget
    const lineItem = select.closest('.quote-line-item')
    const transactionType = select.value

    // Find the transaction type indicator
    const indicator = lineItem.querySelector('.transaction-type-indicator')
    if (!indicator) return

    // Update the indicator based on the selected transaction type
    if (transactionType === 'buy') {
      indicator.className = 'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 transaction-type-indicator'
      indicator.title = 'Buy'
      indicator.setAttribute('data-transaction-type', 'buy')

      // Update the icon and text
      const icon = indicator.querySelector('svg')
      if (icon) {
        icon.innerHTML = 'M5 10l7-7m0 0l7 7m-7-7v18' // arrow_up path
      }
      indicator.innerHTML = `<svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path></svg>Buy`
    } else {
      indicator.className = 'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 transaction-type-indicator'
      indicator.title = 'Sell'
      indicator.setAttribute('data-transaction-type', 'sell')

      // Update the icon and text
      const icon = indicator.querySelector('svg')
      if (icon) {
        icon.innerHTML = 'M19 14l-7 7-7-7m7 7v-18' // arrow_down path
      }
      indicator.innerHTML = `<svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7-7-7m7 7v-18"></path></svg>Sell`
    }
  }

  updateInventoryData(lineItem, data) {
    if (!data.inventory) return

    const lineItemIndex = this.extractLineItemIndex(lineItem)
    if (!lineItemIndex) return

    // Update hidden fields
    const inventoryMargin = data.inventory.inventory_margin !== undefined ? data.inventory.inventory_margin : ''
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_margin', inventoryMargin)
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_price', data.inventory.inventory_price || '')
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_fetched_at', new Date().toISOString())

    // Reset margin tracking for precise calculations on inventory update
    if (data.inventory.inventory_margin !== undefined) {
      lineItem.dataset.preciseInventoryMargin = data.inventory.inventory_margin
      lineItem.dataset.marginManuallyChanged = 'false'
      console.log('Reset margin tracking on inventory update - will use precise value:', data.inventory.inventory_margin)
    }

    // Update display
    this.updateInventoryDisplay(lineItem)

    // Remove stale warning if present
    const staleWarning = lineItem.querySelector('.text-amber-600')
    if (staleWarning && staleWarning.textContent.includes('Stale data')) {
      staleWarning.remove()
    }

    // Recalculate spread if margin is present
    if (this.getLineItemValue(lineItem, 'margin') && data.inventory.inventory_margin) {
      this.calculateAndUpdateSpreadFromMargin(lineItem)
    }
  }

  async updateInventoryDataAndRecalculate(lineItem, data, preservedSpread) {
    if (!data.inventory) return

    const lineItemIndex = this.extractLineItemIndex(lineItem)
    if (!lineItemIndex) return

    // Update hidden fields
    const inventoryMargin = data.inventory.inventory_margin !== undefined ? data.inventory.inventory_margin : ''
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_margin', inventoryMargin)
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_price', data.inventory.inventory_price || '')
    this.createOrUpdateHiddenField(lineItem, lineItemIndex, 'inventory_fetched_at', new Date().toISOString())

    // Reset margin tracking for precise calculations on refresh
    if (data.inventory.inventory_margin !== undefined) {
      lineItem.dataset.preciseInventoryMargin = data.inventory.inventory_margin
      lineItem.dataset.marginManuallyChanged = 'false'
      console.log('Reset margin tracking on refresh/recalculate - will use precise value:', data.inventory.inventory_margin)
    }

    // Update display
    this.updateInventoryDisplay(lineItem)

    // Remove stale warning if present
    const staleWarning = lineItem.querySelector('.text-amber-600')
    if (staleWarning && staleWarning.textContent.includes('Stale data')) {
      staleWarning.remove()
    }

    // Update capital price if inventory price is available
    if (data.initial_price !== undefined) {
      console.log('Updating capital price from refresh:', data.initial_price)
      this.setLineItemValue(lineItem, 'price', parseFloat(data.initial_price).toFixed(3))

      // Clear premium field when refreshing to inventory values
      // This ensures consistency - we're resetting to base inventory pricing
      const premiumInput = lineItem.querySelector('.premium-input')
      if (premiumInput) {
        console.log('Clearing premium field on refresh to maintain consistency')
        premiumInput.value = ''
        premiumInput.dataset.cleared = 'true'
      }

      // Reset spread to 0 when refreshing to inventory (since we're at base inventory price)
      console.log('Setting spread to 0 for inventory-based pricing')
      this.setLineItemValue(lineItem, 'spread', '0.000')

      // Set yield and margin from refresh data (don't recalculate - use exact inventory values)
      if (data.initial_yield !== undefined) {
        this.setLineItemValue(lineItem, 'yield', parseFloat(data.initial_yield).toFixed(3))
        console.log('Set yield from inventory:', data.initial_yield)
      }

      if (data.initial_margin !== undefined) {
        this.setLineItemValue(lineItem, 'margin', parseFloat(data.initial_margin).toFixed(3))
        console.log('Set margin from inventory:', data.initial_margin)

        // Store the initial margin for spread calculations
        lineItem.dataset.initialMargin = (parseFloat(data.initial_margin) / 100).toFixed(6)
        console.log('Restored initialMargin after reset:', lineItem.dataset.initialMargin)
      }

      // Update pricing display with the inventory values
      await this.updatePricingDisplay(lineItem)
    }
  }

  async showInventoryComparisonDialog(currentMargin, newMargin) {
    const currentPercent = (parseFloat(currentMargin) * 100).toFixed(4)
    const newPercent = (newMargin * 100).toFixed(4)

    const message = `Inventory margin has changed:\n\n` +
                   `Current: ${currentPercent}%\n` +
                   `New: ${newPercent}%\n\n` +
                   `Update to new value?`

    return confirm(message)
  }

  showRefreshMessage(lineItem, message, type) {
    let messageContainer = lineItem.querySelector('.refresh-message')
    if (!messageContainer) {
      messageContainer = document.createElement('div')
      messageContainer.className = 'refresh-message text-xs mt-1'
      const pricingSection = lineItem.querySelector('.mt-4.pt-3')
      if (pricingSection) {
        pricingSection.appendChild(messageContainer)
      }
    }

    messageContainer.textContent = message
    messageContainer.className = `refresh-message text-xs mt-1 ${
      type === 'success' ? 'text-success-600' :
      type === 'warning' ? 'text-amber-600' :
      'text-error-600'
    }`

    setTimeout(() => messageContainer.remove(), 3000)
  }

  resetSwapAndShortRatesFromAPI(lineItem, data) {
    // Reset swap and short rates to their initial values from the API response
    if (data.market_price) {
      const marketPrice = data.market_price

      // Market price swap rates come as decimals from backend, convert to percentage for storage
      const swapRatePercent = marketPrice.swap_rate !== null && marketPrice.swap_rate !== undefined
        ? marketPrice.swap_rate * 100
        : null
      const shortRatePercent = marketPrice.short_rate !== null && marketPrice.short_rate !== undefined
        ? marketPrice.short_rate * 100
        : null

      // Update the swap rates using the existing method
      this.updateSwapRates(lineItem, swapRatePercent, shortRatePercent)
    }
  }

  // ============== AUTO-REFRESH STALE DATA ==============
  async checkAndAutoRefreshStaleData() {
    // Check if auto-refresh is enabled via data attribute (set by server based on feature flag)
    const autoRefreshEnabled = this.element.dataset.autoRefreshInventory === 'true'

    if (!autoRefreshEnabled) {
      console.log('Auto-refresh inventory is disabled')
      return
    }

    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    const staleLineItems = []

    lineItems.forEach(lineItem => {
      const fetchedAtField = lineItem.querySelector('input[name*="[inventory_fetched_at]"]')
      if (fetchedAtField && fetchedAtField.value) {
        const fetchedAt = new Date(fetchedAtField.value)
        const now = new Date()
        const minutesOld = (now - fetchedAt) / (1000 * 60)

        if (minutesOld > this.autoRefreshThresholdMinutes) {
          staleLineItems.push(lineItem)
        }
      } else {
        // No fetched_at means it's never been fetched
        const securityId = this.getLineItemValue(lineItem, 'security_id')
        if (securityId) {
          staleLineItems.push(lineItem)
        }
      }
    })

    if (staleLineItems.length > 0) {
      console.log(`Found ${staleLineItems.length} line items with stale inventory data. Auto-refreshing...`)

      // Show notification using TailwindCSS classes
      const notification = document.createElement('div')
      notification.className = 'fixed top-4 right-4 bg-info-100 border border-info-400 text-info-700 px-4 py-3 rounded z-50 flex items-center'
      notification.innerHTML = `
        <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Auto-refreshing inventory data for ${staleLineItems.length} item(s)...</span>
      `
      document.body.appendChild(notification)

      try {
        // Refresh all stale items in parallel
        const refreshPromises = staleLineItems.map(async (lineItem) => {
          const securityId = this.getLineItemValue(lineItem, 'security_id')
          if (securityId) {
            const lineItemData = this.extractLineItemData(lineItem)
            const transactionType = lineItemData.transactionType

            try {
              const data = await this.pricingAPI.fetchMarketPricing(
                securityId,
                transactionType,
                this.getQuoteSettlementDate()
              )

              if (data.inventory?.inventory_margin !== null) {
                this.updateInventoryData(lineItem, data)
              }
            } catch (error) {
              console.error(`Failed to auto-refresh inventory for security ${securityId}:`, error)
            }
          }
        })

        await Promise.all(refreshPromises)

        notification.className = 'fixed top-4 right-4 bg-success-100 border border-success-400 text-success-700 px-4 py-3 rounded z-50 flex items-center'
        notification.innerHTML = `
          <svg class="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          <span>Inventory data refreshed successfully</span>
        `

        setTimeout(() => notification.remove(), 3000)
      } catch (error) {
        console.error('Error during auto-refresh:', error)
        notification.className = 'fixed top-4 right-4 bg-error-100 border border-error-400 text-error-700 px-4 py-3 rounded z-50 flex items-center'
        notification.innerHTML = `
          <svg class="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
          <span>Failed to refresh some inventory data</span>
        `
        setTimeout(() => notification.remove(), 3000)
      }
    }
  }

  // ============== AUTO-POPULATE SECURITIES ==============
  async autoPopulateSecurities() {
    try {
      const autoPopulateData = this.element.dataset.autoPopulateSecurities
      if (!autoPopulateData || autoPopulateData === '[]') {
        return
      }

      const securities = JSON.parse(autoPopulateData)
      if (!Array.isArray(securities) || securities.length === 0) {
        return
      }

      // Add each security sequentially to ensure pricing calculations complete
      for (const security of securities) {
        await this.addSecurityFromData(security)
        // Small delay between securities to ensure DOM updates
        await new Promise(resolve => setTimeout(resolve, 50))
      }

      // Cleanup URL parameter
      this.cleanupUrl()

    } catch (error) {
      console.error('Error auto-populating securities:', error)
    }
  }

  async addSecurityFromData(security) {
    console.log("Auto-adding security:", security)

    if (this.securityExists(security.id)) {
      console.log("Security already added, skipping")
      return
    }

    const timestamp = new Date().getTime()
    const newLineItem = this.createLineItemFromTemplate(timestamp)

    // populateLineItemFromData returns a promise when pricing is fetched
    await this.populateLineItemFromData(newLineItem, security, timestamp)
    this.setLineItemPosition(newLineItem)
    this.updateFormIndices()
  }

  populateLineItemFromData(lineItem, security, index) {
    // Set security ID
    this.setLineItemValue(lineItem, 'security_id', security.id)

    // Store par value pricing
    lineItem.dataset.parValuePricing = security.par_value_pricing || DEFAULT_PAR_VALUE_PRICING

    // Store currency code - auto-populate data might not have it, so default to AUD
    lineItem.dataset.currencyCode = security.currency_code || 'AUD'

    // Store ISIN for security searches
    lineItem.dataset.securityIsin = security.isin

    // Store minimum denomination and parcel size
    if (security.min_denomination) {
      lineItem.dataset.minimumDenomination = security.min_denomination
    }
    if (security.min_parcel_size) {
      lineItem.dataset.minimumParcelSize = security.min_parcel_size
    }

    // Get line number
    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    const lineNumber = lineItems.length

    // Update security display
    const display = lineItem.querySelector('.security-display')
    if (display) {
      let greyMarketBadge = ''
      if (security.is_grey_market) {
        const label = security.grey_market_label || 'Grey Market'
        greyMarketBadge = `
          <div class="mt-1">
            <div class="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 border-purple-200 border rounded-full font-medium">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>${label}</span>
            </div>
          </div>
        `
      }
      const inventoryAvailable = security.inventory_available || 0

      display.innerHTML = this.renderSecurityDisplay(security, inventoryAvailable, greyMarketBadge)
    }

    // Set default values
    // Don't set price/yield/margin to empty - let fetchMarketPricing set them
    this.setLineItemValue(lineItem, 'spread', '0.000')
    this.setLineItemValue(lineItem, 'transaction_type', 'buy')

    // Set face value to effective minimum parcel size (MAX of company and security minimum)
    const effectiveMinParcel = this.getEffectiveMinimumParcelSize(security.min_parcel_size)
    const parValue = parseFloat(lineItem.dataset.parValuePricing) || DEFAULT_PAR_VALUE_PRICING
    const defaultFaceValue = effectiveMinParcel > 0 ? effectiveMinParcel : 1000 * parValue // Default was 1000 volume
    this.setLineItemValue(lineItem, 'quantity', Math.round(defaultFaceValue).toString())

    // Set S&P rating in Security Rating field (in the extra info section)
    const extraInfo = lineItem.querySelector('.extra-info')
    if (extraInfo) {
      // Find the Security Rating input by looking for the label first
      const ratingLabel = Array.from(extraInfo.querySelectorAll('label')).find(label =>
        label.textContent.trim() === 'Security Rating'
      )
      if (ratingLabel) {
        const ratingInput = ratingLabel.parentElement.querySelector('input[type="text"]')
        if (ratingInput) {
          ratingInput.value = security.sp_rating || 'NR'
          console.log(`Set security rating for auto-populated security to: ${security.sp_rating || 'NR'}`)
        }
      }
    }

    // Initialize settlement
    this.initializeLineItemSettlement(lineItem)

    // Fetch and display market pricing, then validate
    // Return the promise so the caller can wait for it
    return this.fetchMarketPricing(lineItem, security.id).then(() => {
      // Return a promise that resolves after the timeout
      return new Promise(resolve => {
        setTimeout(() => {
          // First update line item calculations to ensure all values are computed
          this.updateLineItemCalculations(lineItem)
          // Update pricing display for accrued interest and totals
          this.updatePricingDisplay(lineItem)
          // Update counts after all calculations are done
          this.updateCounts()

          // Validate face value after inventory data is loaded
          const faceValueInput = lineItem.querySelector('.face-value-input')
          if (faceValueInput && faceValueInput.value) {
            this.validateFaceValue({ target: faceValueInput })
          }

          // Validate spread after all pricing data is loaded
          const spreadInput = lineItem.querySelector('.spread-input')
          if (spreadInput && spreadInput.value) {
            const spreadValue = parseFloat(spreadInput.value) || 0
            this.validateSpread(lineItem, spreadValue)
          }

          resolve()
        }, 50)
      })
    })
  }

  cleanupUrl() {
    try {
      const url = new URL(window.location)
      url.searchParams.delete('isins')

      // Update URL without triggering a page reload
      window.history.replaceState({}, '', url.toString())
    } catch (error) {
      console.warn('Could not clean up URL:', error)
    }
  }

  // ============== HELPER METHODS ==============
  initializeLineItemCalculations() {
    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    lineItems.forEach(lineItem => {
      // Store existing swap rates
      const swapRateInput = lineItem.querySelector('input[name*="[swap_rate]"]')
      const shortRateInput = lineItem.querySelector('input[name*="[short_rate]"]')
      const swapRate = this.getSwapRateValue(lineItem, swapRateInput)
      const shortRate = this.getShortRateValue(lineItem, shortRateInput)
      if (swapRate !== null) lineItem.dataset.swapRate = swapRate
      if (shortRate !== null) lineItem.dataset.shortRate = shortRate

      // Store the initial capital price for premium calculations
      const priceInput = lineItem.querySelector('.price-input')
      if (priceInput && priceInput.value && !lineItem.dataset.initialCapitalPrice) {
        lineItem.dataset.initialCapitalPrice = priceInput.value
        console.log(`Stored initial capital price: ${priceInput.value}`)
      }

      // For non-inventory securities, also store the zero-spread price as baseline
      // This is the price before any spread adjustment
      const inventoryMarginInput = lineItem.querySelector('input[name*="[inventory_margin]"]')
      const inventoryMarginValue = inventoryMarginInput ? inventoryMarginInput.value : null
      const hasInventory = inventoryMarginValue !== null || lineItem.querySelector('input[name*="[inventory_price]"]')?.value
      if (!hasInventory && priceInput && priceInput.value) {
        // Check if spread is currently zero
        const spreadInput = lineItem.querySelector('.spread-input')
        const currentSpread = spreadInput ? parseFloat(spreadInput.value) || 0 : 0

        if (Math.abs(currentSpread) < 0.0001) {
          // Store this as the zero-spread baseline price
          lineItem.dataset.zeroSpreadPrice = priceInput.value
          console.log(`Stored zero-spread baseline price for non-inventory security: ${priceInput.value}`)
        }
      }

      // Fetch security data
      const securityId = this.getLineItemValue(lineItem, 'security_id')
      if (securityId) {
        console.log(`Initializing line item for security ${securityId}`)

        this.fetchSecurityDetails(lineItem, securityId).then(() => {
          this.updateLineItemCalculations(lineItem)
          this.updatePricingDisplay(lineItem)
          this.updateInventoryAvailabilityDisplay(lineItem)
          return this.fetchMarketPricing(lineItem, securityId)
        }).then(() => {
          // Only recalculate spread if margin exists but spread doesn't
          // This prevents overwriting saved spread values when editing existing quotes
          const spreadField = lineItem.querySelector('input[name*="[spread]"]')
          const marginField = lineItem.querySelector('input[name*="[margin]"]')

          // Check if we have actual values in the fields
          const hasSpread = spreadField && spreadField.value && spreadField.value !== ''
          const hasMargin = marginField && marginField.value && marginField.value !== ''

          // For existing quotes with both margin and spread, calculate the base margin
          // This is needed for future spread/margin recalculations
          if (hasMargin && hasSpread && !lineItem.dataset.initialMargin) {
            const margin = parseFloat(marginField.value) / 100
            const spread = parseFloat(spreadField.value) / 100
            const transactionType = this.getLineItemValue(lineItem, 'transaction_type') || 'buy'

            // Calculate what the base margin must be for this spread
            const baseMargin = transactionType === TRANSACTION_TYPES.BUY
              ? margin + spread
              : margin - spread

            lineItem.dataset.initialMargin = baseMargin.toFixed(6)
            console.log('Calculated base margin from existing values:', {
              margin: margin,
              spread: spread,
              baseMargin: baseMargin,
              transactionType: transactionType
            })
          }

          if (hasMargin && !hasSpread) {
            console.log('No spread found, calculating from margin')
            this.calculateAndUpdateSpreadFromMargin(lineItem)
          } else if (hasSpread) {
            console.log('Using existing spread value:', spreadField.value)
            // Don't recalculate - keep the saved spread value
          }

          // Validate face value after loading
          const faceValueInput = lineItem.querySelector('.face-value-input')
          if (faceValueInput && faceValueInput.value) {
            this.validateFaceValue({ target: faceValueInput })
          }
        }).catch(error => {
          console.error('Error initializing line item:', error)
        })
      } else {
        this.updateLineItemCalculations(lineItem)
        this.updatePricingDisplay(lineItem)
      }
    })
  }

  async fetchSecurityDetails(lineItem, securityId) {
    // For existing line items, the security data is already in the HTML attributes
    // Only fetch details for new line items that don't have the data yet
    if (lineItem.dataset.parValuePricing) {
      console.log(`Security ${securityId} details already loaded from server`)
      return
    }

    try {
      // This should only be called for new securities added via search
      // which would have the security data embedded in the search results
      console.log(`Security ${securityId} details not found, using defaults`)
      lineItem.dataset.parValuePricing = DEFAULT_PAR_VALUE_PRICING
    } catch (error) {
      console.error('Error setting security details:', error)
      lineItem.dataset.parValuePricing = DEFAULT_PAR_VALUE_PRICING
    }
  }

  getQuoteSettlementDate() {
    const settlementType = this.hasSettlementTypeTarget ? this.settlementTypeTarget.value : null
    const negotiatedDate = this.hasSettlementDateInputTarget ? this.settlementDateInputTarget.value : null

    return PricingCalculations.calculateSettlementDate(settlementType, negotiatedDate)
  }

  // Debounced settlement date update to prevent intermediate invalid dates
  debouncedSettlementDateUpdate(lineItem, delay = 500) {
    const lineItemId = this.getLineItemId(lineItem)

    // Clear any existing timeout for this line item
    if (this.settlementDateTimeouts.has(lineItemId)) {
      clearTimeout(this.settlementDateTimeouts.get(lineItemId))
    }

    // Set new timeout
    const timeoutId = setTimeout(() => {
      console.log('Debounced settlement date update triggered for line item', lineItemId)
      this.updatePricingDisplay(lineItem)
      this.settlementDateTimeouts.delete(lineItemId)
    }, delay)

    this.settlementDateTimeouts.set(lineItemId, timeoutId)
  }

  getLineItemId(lineItem) {
    // Use the line item's unique identifier (index or existing ID)
    return lineItem.dataset.lineItemId || lineItem.querySelector('input[name*="[id]"]')?.value ||
           Array.from(lineItem.parentNode.children).indexOf(lineItem)
  }

  formatDateForInput(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  updateSecurityNumber(item, number) {
    const securityNumber = item.querySelector('.security-number')
    if (securityNumber) {
      securityNumber.textContent = number
    }
  }

  clearFieldFlags(lineItem) {
    ['price-input', 'yield-input', 'margin-input', 'spread-input'].forEach(className => {
      const field = lineItem.querySelector(`.${className}`)
      if (field) {
        field.removeAttribute('data-cleared')
        field.removeAttribute('data-user-changed')
      }
    })
  }

  clearSearchInput() {
    const searchInput = document.querySelector('[data-securities--security-search-target="input"]')
    if (searchInput) {
      searchInput.value = ''
      searchInput.dispatchEvent(new Event('input', { bubbles: true }))
    }

    const selectedDisplay = document.querySelector('[data-securities--security-search-target="selectedDisplay"]')
    if (selectedDisplay) {
      selectedDisplay.classList.add('hidden')
    }

    const selectedList = document.querySelector('[data-securities--security-search-target="selectedList"]')
    if (selectedList) {
      selectedList.innerHTML = ''
    }
  }

  validateSpread(lineItem, spread) {
    const spreadWarning = lineItem.querySelector('.spread-warning')
    if (!spreadWarning) return

    const minSpread = parseFloat(this.element.dataset.spreadMin) || 0
    const maxSpread = parseFloat(this.element.dataset.spreadMax) || 100
    const commissionType = this.element.dataset.commissionType || 'margin-spread'

    // Only validate spread for margin-spread mode
    if (commissionType !== 'margin-spread') {
      // Hide warning for non-margin modes
      spreadWarning.textContent = ''
      spreadWarning.classList.add('hidden')
      return
    }

    const errors = []

    // Round to 3 decimal places to avoid floating point precision issues
    const roundedSpread = Math.round(spread * 1000) / 1000
    const roundedMinSpread = Math.round(minSpread * 1000) / 1000
    const roundedMaxSpread = Math.round(maxSpread * 1000) / 1000

    // For margin-spread, validate as percentage
    if (minSpread !== null && roundedSpread < roundedMinSpread) {
      errors.push(`Spread must be at least ${minSpread}%`)
    }

    if (maxSpread !== null && roundedSpread > roundedMaxSpread) {
      errors.push(`Spread cannot exceed ${maxSpread}%`)
    }

    if (errors.length > 0) {
      spreadWarning.textContent = errors.join(', ')
      spreadWarning.classList.remove('hidden')
    } else {
      // Clear the warning when value is valid
      spreadWarning.textContent = ''
      spreadWarning.classList.add('hidden')
    }
  }

  validatePremium(lineItem, premium) {
    const premiumWarning = lineItem.querySelector('.premium-warning')
    if (!premiumWarning) return

    const minPremium = parseFloat(this.element.dataset.spreadMin) || 0
    const maxPremium = parseFloat(this.element.dataset.spreadMax) || 1.65
    const commissionType = this.element.dataset.commissionType || 'margin-spread'

    // Only validate premium for capital-price-premium mode
    if (commissionType !== 'capital-price-premium') {
      // Hide warning for non-capital-price modes
      premiumWarning.textContent = ''
      premiumWarning.classList.add('hidden')
      return
    }

    const errors = []

    // Round to 3 decimal places to avoid floating point precision issues
    const roundedPremium = Math.round(premium * 1000) / 1000
    const roundedMinPremium = Math.round(minPremium * 1000) / 1000
    const roundedMaxPremium = Math.round(maxPremium * 1000) / 1000

    // Validate premium as dollar amount
    if (minPremium !== null && roundedPremium < roundedMinPremium) {
      errors.push(`Premium must be at least $${minPremium.toFixed(2)}`)
    }

    if (maxPremium !== null && roundedPremium > roundedMaxPremium) {
      errors.push(`Premium cannot exceed $${maxPremium.toFixed(2)}`)
    }

    if (errors.length > 0) {
      premiumWarning.textContent = errors.join(', ')
      premiumWarning.classList.remove('hidden')
    } else {
      // Clear the warning when value is valid
      premiumWarning.textContent = ''
      premiumWarning.classList.add('hidden')
    }
  }

  validateFaceValue(event) {
    const lineItem = event.target.closest('.quote-line-item')
    if (!lineItem) return

    const faceValueInput = event.target
    const faceValue = parseFloat(faceValueInput.value) || 0
    const parValue = parseFloat(lineItem.dataset.parValuePricing) || DEFAULT_PAR_VALUE_PRICING
    const quantity = this.convertFaceValueToQuantity(faceValue, parValue)

    // Get minimum denomination for this security
    const minDenomination = this.getMinimumDenominationForLineItem(lineItem)

    // First update all reserved quantities
    this.calculateReservedQuantities()

    // Get available quantity considering other line items
    const availableQuantity = this.getAvailableQuantityForLineItem(lineItem)
    const warningDiv = lineItem.querySelector('[data-inventory-warning]')

    if (!warningDiv) return

    // Clear previous warnings
    warningDiv.classList.add('hidden')
    warningDiv.classList.remove('text-error-600', 'text-amber-600', 'text-neutral-600')

    // Face value can be decimal, no need to check for whole number

    // Get minimum parcel size for this security
    const minParcelSize = this.getMinimumParcelSizeForLineItem(lineItem)

    // Check minimum parcel size (now in dollars)
    if (minParcelSize > 0 && faceValue > 0 && faceValue < minParcelSize) {
      warningDiv.textContent = `Minimum face value is $${minParcelSize.toLocaleString()}`
      warningDiv.classList.remove('hidden')
      warningDiv.classList.add('text-error-600')
      return
    }

    // Check divisibility by minimum denomination (now in dollars)
    if (minDenomination > 0 && faceValue > 0 && faceValue % minDenomination !== 0) {
      warningDiv.textContent = `Face value must be divisible by $${minDenomination.toLocaleString()} (minimum denomination)`
      warningDiv.classList.remove('hidden')
      warningDiv.classList.add('text-error-600')
      return
    }

    if (availableQuantity === 0) {
      warningDiv.textContent = 'No inventory available'
      warningDiv.classList.remove('hidden')
      warningDiv.classList.add('text-neutral-600')
    } else if (quantity > availableQuantity) {
      warningDiv.textContent = `Exceeds available inventory (${availableQuantity.toLocaleString()})`
      warningDiv.classList.remove('hidden')
      warningDiv.classList.add('text-error-600')
    } else if (availableQuantity < 100000 && quantity > 0) {
      warningDiv.textContent = `Limited inventory (${availableQuantity.toLocaleString()} available)`
      warningDiv.classList.remove('hidden')
      warningDiv.classList.add('text-amber-600')
    }

    // Update all inventory displays to reflect new reserved quantities
    this.updateAllInventoryDisplays()
  }

  updateInventoryAvailabilityDisplay(lineItem) {
    // Update the inventory icon based on available quantity
    const inventoryAvailable = parseFloat(lineItem.dataset.inventoryAvailableQuantity) || 0
    const inventoryIndicator = lineItem.querySelector('.inventory-indicator')

    if (!inventoryIndicator) return

    let iconName, iconColor, titleText

    if (inventoryAvailable > 0) {
      if (inventoryAvailable >= 100000) {
        iconName = 'circle_check_mark'
        iconColor = 'text-green-600'
        titleText = `Inventory (${inventoryAvailable.toLocaleString()})`
      } else {
        iconName = 'exclamation_triangle'
        iconColor = 'text-warning-600'
        titleText = `Limited (${inventoryAvailable.toLocaleString()})`
      }
    } else {
      iconName = 'cross_mark'
      iconColor = 'text-red-600'
      titleText = 'No inventory'
    }

    // Update data attributes on both line item and indicator
    lineItem.dataset.inventoryAvailableQuantity = inventoryAvailable
    inventoryIndicator.dataset.inventoryAvailableQuantity = inventoryAvailable
    inventoryIndicator.title = titleText

    // Update icon SVG (IconComponent renders an SVG with a path element)
    const iconSpan = inventoryIndicator.querySelector('span')
    if (iconSpan) {
      iconSpan.className = iconColor
      const svg = iconSpan.querySelector('svg')
      if (svg) {
        const path = svg.querySelector('path')
        if (path) {
          const iconPath = this.getIconPath(iconName)
          if (iconPath) {
            path.setAttribute('d', iconPath)
          }
        }
      }
    }
  }

  // Load inventory data for existing line items (when editing a quote)
  async loadInventoryForExistingLineItems() {
    if (!this.hasLineItemsTarget) return

    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    const promises = []

    lineItems.forEach(lineItem => {
      const securityId = this.getLineItemValue(lineItem, 'security_id')
      if (securityId) {
        // Check if inventory data is already loaded (has inventory_price or inventory_margin)
        const inventoryPriceInput = lineItem.querySelector('input[name*="[inventory_price]"]')
        const hasInventoryData = inventoryPriceInput && inventoryPriceInput.value

        // Only fetch if we don't have inventory data yet
        // This prevents unnecessary API calls when data is already present
        if (!hasInventoryData) {
          console.log(`Loading inventory for existing line item with security ${securityId}`)
          promises.push(this.fetchMarketPricing(lineItem, securityId, true))
        } else {
          // Update icon display with existing data
          const inventoryAvailable = parseFloat(lineItem.dataset.inventoryAvailableQuantity) || 0
          if (inventoryAvailable > 0) {
            this.updateInventoryAvailabilityDisplay(lineItem)
          }
        }
      }
    })

    // Wait for all inventory loads to complete
    if (promises.length > 0) {
      await Promise.all(promises)
      console.log('Finished loading inventory for existing line items')
    }
  }

  setLineItemPosition(lineItem) {
    const currentCount = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])').length
    this.setLineItemValue(lineItem, 'position', currentCount - 1)
    console.log(`Set position for new line item to ${currentCount - 1}`)
  }

  // Document selection is now handled via the centralized modal
  // (removed old line-item level document selection functions)

  // ============== TRANSACTION TYPE HANDLING ==============
  async handleTransactionTypeChange(lineItem) {
    const securityId = this.getLineItemValue(lineItem, 'security_id')
    if (!securityId) return

    const transactionType = this.getLineItemValue(lineItem, 'transaction_type') || 'buy'
    console.log(`Transaction type changed to ${transactionType} for security ${securityId}`)

    lineItem.dataset.updatingTransactionType = 'true'

    // Re-fetch market pricing with new transaction type
    await this.fetchMarketPricing(lineItem, securityId)

    // Recalculate margin from spread since the formula changes based on transaction type
    // The spread (broker's profit) should stay the same, but margin needs to be recalculated
    const spreadInput = lineItem.querySelector('.spread-input')
    if (spreadInput && spreadInput.value) {
      console.log('Recalculating margin after transaction type change')
      this.calculateAndUpdateMarginFromSpread(lineItem)
    }

    // Update all inventory displays since availability depends on transaction type
    this.updateAllInventoryDisplays()

    // Re-validate quantity
    const quantityInput = lineItem.querySelector('.quantity-input')
    if (quantityInput && quantityInput.value) {
      this.validateQuantity({ target: quantityInput })
    }

    this.updatePricingDisplay(lineItem)
    lineItem.dataset.updatingTransactionType = 'false'
  }

  // ============== INVENTORY TRACKING ==============
  calculateReservedQuantities() {
    this.reservedQuantities = {}

    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    lineItems.forEach(lineItem => {
      const securityId = this.getLineItemValue(lineItem, 'security_id')
      const quantity = this.getLineItemNumericValue(lineItem, 'quantity', 0)
      const transactionType = this.getLineItemValue(lineItem, 'transaction_type') || 'buy'

      if (securityId && quantity > 0) {
        const key = `${securityId}-${transactionType}`
        this.reservedQuantities[key] = (this.reservedQuantities[key] || 0) + quantity
      }
    })

    console.log('Reserved quantities:', this.reservedQuantities)
    return this.reservedQuantities
  }

  getAvailableQuantityForLineItem(lineItem) {
    const securityId = this.getLineItemValue(lineItem, 'security_id')
    const transactionType = this.getLineItemValue(lineItem, 'transaction_type') || 'buy'
    const baseAvailable = parseFloat(lineItem.dataset.inventoryAvailableQuantity) || 0
    const currentQuantity = this.getLineItemNumericValue(lineItem, 'quantity', 0)

    if (!securityId) return baseAvailable

    // Get total reserved for this security/transaction type
    const key = `${securityId}-${transactionType}`
    const totalReserved = this.reservedQuantities[key] || 0

    // Available = base inventory - (total reserved - current quantity)
    // We subtract current quantity because it's already included in totalReserved
    const available = baseAvailable - (totalReserved - currentQuantity)

    return Math.max(0, available)
  }

  getMinimumDenominationForLineItem(lineItem) {
    const securityId = this.getLineItemValue(lineItem, 'security_id')
    if (!securityId) return 0

    // Try to get from data attribute first
    const minDenomAttr = lineItem.dataset.minimumDenomination
    if (minDenomAttr) {
      const minDenom = parseInt(minDenomAttr)
      return isNaN(minDenom) ? 0 : minDenom
    }

    // No default - return 0 if not specified
    return 0
  }

  getMinimumParcelSizeForLineItem(lineItem) {
    const securityId = this.getLineItemValue(lineItem, 'security_id')
    if (!securityId) return this.companyMinimumParcelSize

    // Try to get from data attribute first
    const minParcelAttr = lineItem.dataset.minimumParcelSize
    const securityMinParcel = minParcelAttr ? parseInt(minParcelAttr) : 0

    // Return the MAX of company and security minimum parcel size
    return this.getEffectiveMinimumParcelSize(securityMinParcel)
  }

  updateAllInventoryDisplays() {
    this.calculateReservedQuantities()

    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    lineItems.forEach(lineItem => {
      this.updateAvailableQuantityDisplay(lineItem)
    })
  }

  updateAvailableQuantityDisplay(lineItem) {
    // No longer displaying availability text to maintain clean design
    // Availability is tracked internally for validation purposes
  }

  // Handle Enter key in input fields - prevent form submission and blur the field instead
  handleEnterKey(event) {
    if (event.key === 'Enter' || event.keyCode === 13) {
      event.preventDefault()
      event.target.blur() // This will trigger the blur event and finish editing
    }
  }

  // Handle pricing calculation selection change (PLAT-970)
  handlePricingCalculationChange(event) {
    const selectedCalculation = event.target.value
    console.log('Pricing calculation changed to:', selectedCalculation)

    // PLAT-970: Refresh all line items with new pricing calculation
    // This updates the margin labels and values based on user selection
    this.refreshAllLineItemPricing()
  }

  // PLAT-970: Get selected pricing calculation (margin, ispread, gspread)
  getSelectedPricingCalculation() {
    if (this.hasPricingCalculationSelectTarget) {
      return this.pricingCalculationSelectTarget.value
    }
    return 'margin' // Default to margin if no selector
  }

  // PLAT-970: Refresh pricing for all line items with current pricing calculation
  async refreshAllLineItemPricing() {
    const lineItems = this.lineItemsTarget.querySelectorAll('.quote-line-item:not([style*="display: none"])')
    const selectedCalculation = this.getSelectedPricingCalculation()

    console.log(`Refreshing ${lineItems.length} line items with pricing calculation: ${selectedCalculation}`)

    for (const lineItem of lineItems) {
      // PLAT-971: Update the hidden pricing_calculation field so it persists when quote is saved
      const pricingCalcInput = lineItem.querySelector('input[name*="[pricing_calculation]"]')
      if (pricingCalcInput) {
        pricingCalcInput.value = selectedCalculation
      }

      const securityId = this.getLineItemValue(lineItem, 'security_id')
      if (securityId) {
        await this.fetchMarketPricing(lineItem, securityId, true) // isRecalculation = true
        this.updateLineItemCalculations(lineItem)
        this.updatePricingDisplay(lineItem)
      }
    }
  }

  // PLAT-970: Display margin with metadata (label, badge, tooltip, note)
  displayMarginWithMetadata(lineItem, pricingData) {
    const marginDisplay = lineItem.querySelector('.market-margin-display')
    if (!marginDisplay) return

    // Get margin value and metadata
    const marginValue = pricingData.market_margin !== undefined
      ? `${(parseFloat(pricingData.market_margin) * 100).toFixed(4)}%`
      : '-'

    const metadata = pricingData.margin_metadata

    if (metadata) {
      // Display margin with label and badge
      const label = metadata.margin_label || 'BA Margin'
      const dataSource = metadata.data_source || 'cached'
      const tooltip = this.getMarginTooltipText(metadata.margin_type)

      // Escape HTML in tooltip messages
      const escapeHtml = (text) => text.replace(/"/g, '&quot;').replace(/'/g, '&#39;')

      // Build fallback warning icon if needed (appears next to label)
      // Use a separate group to prevent tooltip overlap with label tooltip
      const fallbackIcon = metadata.fallback_used
        ? `<span class="relative group/warning inline-block text-warning-600 cursor-help ml-0.5">
             ⚠️
             <span class="invisible group-hover/warning:visible absolute left-0 top-5 w-64 p-2 bg-neutral-900 text-white text-xs rounded shadow-lg z-50 whitespace-normal">
               ${escapeHtml(metadata.fallback_reason)}
             </span>
           </span>`
        : ''

      // Build info icon for market data note (appears next to badge)
      // Use a separate group to prevent tooltip overlap with badge
      const infoIcon = metadata.note
        ? `<span class="relative group/info inline-block text-neutral-500 cursor-help ml-0.5">
             ℹ️
             <span class="invisible group-hover/info:visible absolute left-0 top-5 w-64 p-2 bg-neutral-900 text-white text-xs rounded shadow-lg z-50 whitespace-normal">
               ${escapeHtml(metadata.note)}
             </span>
           </span>`
        : ''

      // Two-row layout: value on top (aligns with other columns), metadata below
      marginDisplay.innerHTML = `
        <div class="flex flex-col">
          <div class="font-medium">${marginValue}</div>
          <div class="flex items-center gap-1.5 mt-0.5 text-xs">
            <span class="relative group/label inline-block text-neutral-600 cursor-help">
              ${label}
              <span class="invisible group-hover/label:visible absolute left-0 top-5 w-64 p-2 bg-neutral-900 text-white text-xs rounded shadow-lg z-50 whitespace-normal">
                ${escapeHtml(tooltip)}
              </span>
            </span>
            ${fallbackIcon}
            <span class="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              ${dataSource === 'cached' ? 'Market Data' : dataSource}
            </span>
            ${infoIcon}
          </div>
        </div>
      `
    } else {
      // Fallback to simple display if no metadata
      marginDisplay.textContent = marginValue
    }
  }

  // PLAT-970: Get tooltip text for margin types
  getMarginTooltipText(marginType) {
    const tooltips = {
      'ispread': 'I-Spread (Interpolated Spread) measures the yield spread over the interpolated government curve. Based on current market prices.',
      'gspread': 'G-Spread measures the yield spread over a specific government bond with matched maturity. Based on current market prices.',
      'margin': 'BA Margin measures the yield spread over the swap curve'
    }
    return tooltips[marginType] || tooltips['margin']
  }
}
